export const LOCALES = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'fr', label: 'Français (French)', flag: '🇫🇷' },
  { value: 'de', label: 'Deutsch (German)', flag: '🇩🇪' },
  { value: 'es', label: 'Español (Spanish)', flag: '🇪🇸' },
  { value: 'pt', label: 'Português (Portuguese (Brazilian))', flag: '🇧🇷' },
  { value: 'it', label: 'Italiano (Italian)', flag: '🇮🇹' },
  { value: 'th', label: 'ไทย (Thai)', flag: '🇹🇭' },
  { value: 'zh', label: '中文 (Mandarin Chinese)', flag: '🇨🇳' },
  { value: 'hi', label: 'हिन्दी (Hindi)', flag: '🇮🇳' },
  { value: 'ja', label: '日本語 (Japanese)', flag: '🇯🇵' },
] as const;

export type Locale = (typeof LOCALES)[number]['value'];

const COMMON_TRANSLATIONS = {
  brandName: 'Alliance of Galactic Travellers',
  productName: 'AGT Event Calendar',
  agtNavi: 'AGT NAVI',
  nationalHolidays: 'AGT National Holidays',
  closeNationalHolidays: 'Close AGT National Holidays',
  loadingNationalHolidays: 'Loading AGT National Holidays…',
  nationalHolidaysLoadError: 'AGT National Holidays could not be loaded.',
  noNationalHolidays: 'No AGT National Holidays are available.',
} as const;

const english = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'Skip to calendar',
  syncingDatabase: 'Syncing database',
  databaseOffline: 'Database offline',
  databaseLive: 'Database live',
  openSettings: 'Open settings',
  settings: 'Settings',
  contactSupport: 'Contact AGT Support',
  addEvent: 'Add an Event',
  bookmarks: 'Bookmarks',
  bookmarked: 'Bookmarked',
  bookmark: 'Bookmark',
  removeBookmark: 'Remove Bookmark',
  closeBookmarks: 'Close bookmarks',
  bookmarksBackupNotice: 'Bookmarks are stored only in this browser, not in the cloud. They may be lost if you clear browsing data. Use Export to back them up and Import to restore them.',
  noBookmarks: 'No bookmarked events yet.',
  noMatchingBookmarks: 'No bookmarked events match your search.',
  missingBookmarks: '{{count}} bookmarks reference events that no longer exist.',
  removeMissingBookmarks: 'Remove invalid bookmarks',
  bookmarksImported: 'Bookmarks imported and merged.',
  bookmarksExported: 'Bookmarks exported.',
  bookmarksCleared: 'Bookmarks cleared.',
  missingBookmarksRemoved: 'Invalid bookmarks removed.',
  bookmarksImportError: 'Unable to import bookmarks.',
  bookmarksExportError: 'Unable to export bookmarks.',
  bookmarksLoadError: 'Unable to load saved bookmarks.',
  bookmarksSaveError: 'Unable to save bookmarks. Your changes were not applied.',
  invalidBookmarksCsv: 'Bookmarks CSV must contain only an EVENT_ID column with four hexadecimal characters per ID.',
  pageHeading: 'AGT Special Events Calendar',
  pageIntro:
    'Search and browse past and upcoming AGT gatherings. Select any event to view its schedule, portal address, and description.',
  syncHeading: 'Synchronizing the galactic archive',
  syncDescription: 'Fetching a fresh copy of the event database…',
  databaseError: 'Database error',
  eventsCouldNotBeLoaded: 'Events could not be loaded',
  databaseHttpError: 'The event database returned HTTP {{status}}.',
  unexpectedDatabaseError: 'An unexpected database error occurred.',
  databaseInstructions:
    'Check the connection and confirm that the configured CSV is published and permits browser access.',
  retrySync: 'Retry sync',
  databaseStatus: 'Database status',
  validEventRecord: 'valid event record',
  validEventRecords: 'valid event records',
  matchingFilters: 'matching filters',
  lastSync: 'Last sync',
  reloadDatabase: 'Reload Database',
  databaseLoadedWith: 'Database loaded with {{count}} {{warningLabel}}{{skipped}}',
  warning: 'warning',
  warnings: 'warnings',
  malformedRowsSkipped: '; {{count}} malformed rows skipped',
  validEventsRemain:
    'Valid events remain available. Row numbers refer to the source CSV and are shown for maintainers.',
  rowLabel: 'Row {{row}}:',
  archiveEmpty: 'Archive empty',
  noValidEvents: 'No valid events are available',
  emptyArchiveDescription:
    'The CSV loaded successfully but contained no rows that met the required event schema.',
  home: 'Home',
  about: 'About',
  team: 'Team',
  contribute: 'Contribute',
  galacticArchives: 'Galactic Archives',
  engage: 'Engage',
  terms: 'Terms',
  support: 'Support',
  copyright: 'Copyright',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'Viewer local time · {{timeZone}}',
  calendarNavigation: 'Calendar navigation',
  previousMonth: 'Previous month',
  today: 'Today',
  nextMonth: 'Next month',
  goToMonth: 'Go to month',
  noMatchingEvents: 'No matching events intersect this calendar view.',
  matchingEventSummary: '{{count}} matching event intersects this calendar view.',
  matchingEventsSummary: '{{count}} matching events intersect this calendar view.',
  eventCalendar: 'Event calendar',
  starts: 'Starts',
  continues: 'Continues',
  ends: 'Ends',
  event: 'Event',
  eventDetails: '{{name}}. {{context}}. Open details.',
  eventDetailsAtTime: '{{name}}. {{context}} at {{time}}. Open details.',
  archiveControls: 'Galactic archive controls',
  filterEvents: 'Filter events',
  clearAll: 'Clear all',
  clearAllCount: 'Clear all ({{count}})',
  search: 'Search',
  searchPlaceholder: 'Name, ID, location, portal code…',
  eventType: 'Event type',
  allEventTypes: 'All event types',
  host: 'Host',
  allHosts: 'All hosts',
  galaxy: 'Galaxy',
  allGalaxies: 'All galaxies',
  dateContext: 'Date context',
  allDates: 'All dates',
  upcomingOnly: 'Upcoming only',
  next30Days: 'Next 30 days',
  calendarPreferences: 'Calendar preferences',
  closeSettings: 'Close settings',
  changeLog: 'Change Log',
  closeChangeLog: 'Close change log',
  loadingChangeLog: 'Loading change log…',
  changeLogLoadError: 'The change log could not be loaded.',
  noChangeLogEntries: 'No change log entries are available.',
  fontScale: 'Font scale',
  defaultLabel: 'Default',
  fontScaleHelp: 'Scales the complete calendar interface.',
  locale: 'Locale',
  localeHelp: 'Controls localized calendar dates, times, and interface text.',
  localTimezone: 'Local timezone',
  timeZoneHelp: 'Sets event placement and local schedule displays.',
  agtAnthem: 'AGT Anthem',
  muteDefault: 'Mute (Default)',
  unmute: 'Unmute',
  anthemHelp: 'Playback begins after your first interaction.',
  reset: 'Reset',
  import: 'Import',
  export: 'Export',
  settingsExported: 'Settings exported.',
  settingsExportError: 'Unable to export settings.',
  settingsReset: 'Settings reset.',
  settingsImported: 'Settings imported.',
  settingsImportError: 'Unable to import settings.',
  autoTimeZone: 'Auto',
  invalidSettingsJson: 'The selected file is not valid settings JSON.',
  settingsJsonObject: 'Settings JSON must contain an object.',
  unsupportedFontScale: 'Settings JSON contains an unsupported font scale.',
  unsupportedLocale: 'Settings JSON contains an unsupported locale.',
  unsupportedTimezone: 'Settings JSON contains an unsupported timezone.',
  unsupportedAnthem: 'Settings JSON contains an unsupported anthem setting.',
  notSupplied: 'Not supplied',
  eventRecord: 'Event record · {{id}}',
  eventUrlCopied: 'Event URL copied!',
  bookmarkAdded: 'Bookmark added!',
  bookmarkRemoved: 'Bookmark removed!',
  copyUrlError: 'Unable to copy URL',
  share: 'Share',
  closeEventDetails: 'Close event details',
  eventCoverImage: 'Event cover image',
  coverArtworkFor: 'Cover artwork for {{name}}',
  noEventCover: 'No event cover available',
  coverLoadError: 'Cover image could not be loaded',
  noCoverSupplied: 'No cover image supplied',
  schedule: 'Schedule',
  startLocal: 'Start · local',
  startUtc: 'Start · UTC',
  endLocal: 'End · local',
  endUtc: 'End · UTC',
  eventInformation: 'Event information',
  eventId: 'Event ID',
  region: 'Region',
  system: 'System',
  planet: 'Planet',
  portalCode: 'Portal code',
  openGlyphTool: 'Open with Glyph Tool',
  description: 'Description',
  noMarkdownDescription: 'No Markdown description supplied.',
  loadingRemoteDescription: 'Loading remote description…',
  remoteDescriptionError:
    'The remote description could not be loaded. Event details above are still available.',
  relatedLinks: 'Related links',
  noRelatedLinks: 'No related links supplied.',
  linkLabel: 'Link {{number}}',
  fieldRequired: '{{field}} is required',
  eventIdHex: 'EVENT_ID must contain exactly four hexadecimal characters',
  portalCodeLength: 'EVENT_PORTAL_CODE must contain exactly 12 alphanumeric characters',
  startUtcTimestamp: 'EVENT_DATETIME_START must be a valid UTC ISO 8601 timestamp',
  endUtcTimestamp: 'EVENT_DATETIME_END must be a valid UTC ISO 8601 timestamp',
  endBeforeStart: 'EVENT_DATETIME_END cannot be before EVENT_DATETIME_START',
  unsafeDescriptionUrl: 'EVENT_MD_URI was ignored because it is not a safe HTTP(S) URL',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI was ignored because it is not a safe HTTP(S) URL',
  invalidRelatedLink: 'Related link was ignored because it is invalid: {{value}}',
  csvParser: 'CSV parser: {{message}}',
  missingHeaders: 'Missing required CSV headers: {{headers}}',
  duplicateEventId: 'Duplicate EVENT_ID {{id}}; row was skipped',
} as const;

export type Translations = {
  [Key in keyof typeof english]: string;
};

const french: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'Aller au calendrier',
  syncingDatabase: 'Synchronisation de la base de données',
  databaseOffline: 'Base de données hors ligne',
  databaseLive: 'Base de données en ligne',
  openSettings: 'Ouvrir les paramètres',
  settings: 'Paramètres',
  contactSupport: 'Contacter l’assistance AGT',
  addEvent: 'Ajouter un événement',
  bookmarks: 'Favoris',
  bookmarked: 'En favoris',
  bookmark: 'Ajouter aux favoris',
  removeBookmark: 'Retirer des favoris',
  closeBookmarks: 'Fermer les favoris',
  bookmarksBackupNotice: 'Les favoris sont enregistrés uniquement dans ce navigateur, pas dans le cloud. Ils peuvent être perdus si vous effacez les données de navigation. Utilisez Exporter pour les sauvegarder et Importer pour les restaurer.',
  noBookmarks: 'Aucun événement en favori.',
  noMatchingBookmarks: 'Aucun favori ne correspond à votre recherche.',
  missingBookmarks: '{{count}} favoris renvoient à des événements qui n’existent plus.',
  removeMissingBookmarks: 'Supprimer les favoris invalides',
  bookmarksImported: 'Favoris importés et fusionnés.',
  bookmarksExported: 'Favoris exportés.',
  bookmarksCleared: 'Favoris effacés.',
  missingBookmarksRemoved: 'Favoris invalides supprimés.',
  bookmarksImportError: 'Impossible d’importer les favoris.',
  bookmarksExportError: 'Impossible d’exporter les favoris.',
  bookmarksLoadError: 'Impossible de charger les favoris enregistrés.',
  bookmarksSaveError: 'Impossible d’enregistrer les favoris. Vos modifications n’ont pas été appliquées.',
  invalidBookmarksCsv: 'Le CSV des favoris doit contenir uniquement une colonne EVENT_ID avec quatre caractères hexadécimaux par identifiant.',
  pageHeading: 'Calendrier des événements spéciaux AGT',
  pageIntro: 'Recherchez et parcourez les rassemblements AGT passés et à venir. Sélectionnez un événement pour voir son horaire, son adresse de portail et sa description.',
  syncHeading: 'Synchronisation des archives galactiques',
  syncDescription: 'Récupération d’une nouvelle copie de la base de données des événements…',
  databaseError: 'Erreur de base de données',
  eventsCouldNotBeLoaded: 'Impossible de charger les événements',
  databaseHttpError: 'La base de données des événements a renvoyé HTTP {{status}}.',
  unexpectedDatabaseError: 'Une erreur inattendue de base de données s’est produite.',
  databaseInstructions: 'Vérifiez la connexion et confirmez que le CSV configuré est publié et autorise l’accès depuis le navigateur.',
  retrySync: 'Réessayer la synchronisation',
  databaseStatus: 'État de la base de données',
  validEventRecord: 'événement valide',
  validEventRecords: 'événements valides',
  matchingFilters: 'correspondant aux filtres',
  lastSync: 'Dernière synchronisation',
  reloadDatabase: 'Recharger la base de données',
  databaseLoadedWith: 'Base de données chargée avec {{count}} {{warningLabel}}{{skipped}}',
  warning: 'avertissement',
  warnings: 'avertissements',
  malformedRowsSkipped: ' ; {{count}} lignes mal formées ignorées',
  validEventsRemain: 'Les événements valides restent disponibles. Les numéros de ligne font référence au CSV source et sont affichés pour les responsables.',
  rowLabel: 'Ligne {{row}} :',
  archiveEmpty: 'Archives vides',
  noValidEvents: 'Aucun événement valide n’est disponible',
  emptyArchiveDescription: 'Le CSV a été chargé, mais aucune ligne ne respectait le schéma d’événement requis.',
  home: 'Accueil',
  about: 'À propos',
  team: 'Équipe',
  contribute: 'Contribuer',
  galacticArchives: 'Archives galactiques',
  engage: 'Participer',
  terms: 'Conditions',
  support: 'Assistance',
  copyright: 'Droits d’auteur',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'Heure locale du visiteur · {{timeZone}}',
  calendarNavigation: 'Navigation du calendrier',
  previousMonth: 'Mois précédent',
  today: 'Aujourd’hui',
  nextMonth: 'Mois suivant',
  goToMonth: 'Aller au mois',
  noMatchingEvents: 'Aucun événement correspondant dans cette vue du calendrier.',
  matchingEventSummary: '{{count}} événement correspondant dans cette vue du calendrier.',
  matchingEventsSummary: '{{count}} événements correspondants dans cette vue du calendrier.',
  eventCalendar: 'Calendrier des événements',
  starts: 'Débute',
  continues: 'Continue',
  ends: 'Se termine',
  event: 'Événement',
  eventDetails: '{{name}}. {{context}}. Ouvrir les détails.',
  eventDetailsAtTime: '{{name}}. {{context}} à {{time}}. Ouvrir les détails.',
  archiveControls: 'Commandes des archives galactiques',
  filterEvents: 'Filtrer les événements',
  clearAll: 'Tout effacer',
  clearAllCount: 'Tout effacer ({{count}})',
  search: 'Rechercher',
  searchPlaceholder: 'Nom, ID, lieu, code de portail…',
  eventType: 'Type d’événement',
  allEventTypes: 'Tous les types d’événement',
  host: 'Hôte',
  allHosts: 'Tous les hôtes',
  galaxy: 'Galaxie',
  allGalaxies: 'Toutes les galaxies',
  dateContext: 'Période',
  allDates: 'Toutes les dates',
  upcomingOnly: 'À venir uniquement',
  next30Days: '30 prochains jours',
  calendarPreferences: 'Préférences du calendrier',
  closeSettings: 'Fermer les paramètres',
  changeLog: 'Journal des modifications',
  closeChangeLog: 'Fermer le journal des modifications',
  loadingChangeLog: 'Chargement du journal des modifications…',
  changeLogLoadError: 'Impossible de charger le journal des modifications.',
  noChangeLogEntries: 'Aucune entrée du journal des modifications n’est disponible.',
  fontScale: 'Échelle de police',
  defaultLabel: 'Par défaut',
  fontScaleHelp: 'Redimensionne toute l’interface du calendrier.',
  locale: 'Langue et région',
  localeHelp: 'Contrôle les dates, les heures et le texte de l’interface localisés.',
  localTimezone: 'Fuseau horaire local',
  timeZoneHelp: 'Définit le placement des événements et l’affichage des horaires locaux.',
  agtAnthem: 'Hymne AGT',
  muteDefault: 'Muet (par défaut)',
  unmute: 'Activer le son',
  anthemHelp: 'La lecture commence après votre première interaction.',
  reset: 'Réinitialiser',
  import: 'Importer',
  export: 'Exporter',
  settingsExported: 'Paramètres exportés.',
  settingsExportError: 'Impossible d’exporter les paramètres.',
  settingsReset: 'Paramètres réinitialisés.',
  settingsImported: 'Paramètres importés.',
  settingsImportError: 'Impossible d’importer les paramètres.',
  autoTimeZone: 'Automatique',
  invalidSettingsJson: 'Le fichier sélectionné ne contient pas de paramètres JSON valides.',
  settingsJsonObject: 'Le JSON des paramètres doit contenir un objet.',
  unsupportedFontScale: 'Le JSON des paramètres contient une échelle de police non prise en charge.',
  unsupportedLocale: 'Le JSON des paramètres contient une langue non prise en charge.',
  unsupportedTimezone: 'Le JSON des paramètres contient un fuseau horaire non pris en charge.',
  unsupportedAnthem: 'Le JSON des paramètres contient un réglage d’hymne non pris en charge.',
  notSupplied: 'Non renseigné',
  eventRecord: 'Fiche d’événement · {{id}}',
  eventUrlCopied: 'URL de l’événement copiée !',
  bookmarkAdded: 'Favori ajouté !',
  bookmarkRemoved: 'Favori supprimé !',
  copyUrlError: 'Impossible de copier l’URL',
  share: 'Partager',
  closeEventDetails: 'Fermer les détails de l’événement',
  eventCoverImage: 'Image de couverture de l’événement',
  coverArtworkFor: 'Illustration de couverture pour {{name}}',
  noEventCover: 'Aucune couverture disponible',
  coverLoadError: 'Impossible de charger l’image de couverture',
  noCoverSupplied: 'Aucune image de couverture fournie',
  schedule: 'Horaire',
  startLocal: 'Début · local',
  startUtc: 'Début · UTC',
  endLocal: 'Fin · locale',
  endUtc: 'Fin · UTC',
  eventInformation: 'Informations sur l’événement',
  eventId: 'ID de l’événement',
  region: 'Région',
  system: 'Système',
  planet: 'Planète',
  portalCode: 'Code de portail',
  openGlyphTool: 'Ouvrir avec Glyph Tool',
  description: 'Description',
  noMarkdownDescription: 'Aucune description Markdown fournie.',
  loadingRemoteDescription: 'Chargement de la description distante…',
  remoteDescriptionError: 'Impossible de charger la description distante. Les détails de l’événement ci-dessus restent disponibles.',
  relatedLinks: 'Liens associés',
  noRelatedLinks: 'Aucun lien associé fourni.',
  linkLabel: 'Lien {{number}}',
  fieldRequired: '{{field}} est requis',
  eventIdHex: 'EVENT_ID doit contenir exactement quatre caractères hexadécimaux',
  portalCodeLength: 'EVENT_PORTAL_CODE doit contenir exactement 12 caractères alphanumériques',
  startUtcTimestamp: 'EVENT_DATETIME_START doit être un horodatage UTC ISO 8601 valide',
  endUtcTimestamp: 'EVENT_DATETIME_END doit être un horodatage UTC ISO 8601 valide',
  endBeforeStart: 'EVENT_DATETIME_END ne peut pas précéder EVENT_DATETIME_START',
  unsafeDescriptionUrl: 'EVENT_MD_URI a été ignoré car il ne s’agit pas d’une URL HTTP(S) sûre',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI a été ignoré car il ne s’agit pas d’une URL HTTP(S) sûre',
  invalidRelatedLink: 'Le lien associé a été ignoré car il est invalide : {{value}}',
  csvParser: 'Analyseur CSV : {{message}}',
  missingHeaders: 'En-têtes CSV requis manquants : {{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} en double ; ligne ignorée',
};

const german: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'Zum Kalender springen',
  syncingDatabase: 'Datenbank wird synchronisiert',
  databaseOffline: 'Datenbank offline',
  databaseLive: 'Datenbank online',
  openSettings: 'Einstellungen öffnen',
  settings: 'Einstellungen',
  contactSupport: 'AGT-Support kontaktieren',
  addEvent: 'Veranstaltung hinzufügen',
  bookmarks: 'Lesezeichen',
  bookmarked: 'Als Lesezeichen markiert',
  bookmark: 'Lesezeichen setzen',
  removeBookmark: 'Lesezeichen entfernen',
  closeBookmarks: 'Lesezeichen schließen',
  bookmarksBackupNotice: 'Lesezeichen werden nur in diesem Browser gespeichert, nicht in der Cloud. Sie können verloren gehen, wenn Sie die Browserdaten löschen. Verwenden Sie Exportieren zum Sichern und Importieren zum Wiederherstellen.',
  noBookmarks: 'Noch keine gespeicherten Ereignisse.',
  noMatchingBookmarks: 'Keine Lesezeichen entsprechen Ihrer Suche.',
  missingBookmarks: '{{count}} Lesezeichen verweisen auf Ereignisse, die nicht mehr existieren.',
  removeMissingBookmarks: 'Ungültige Lesezeichen entfernen',
  bookmarksImported: 'Lesezeichen importiert und zusammengeführt.',
  bookmarksExported: 'Lesezeichen exportiert.',
  bookmarksCleared: 'Lesezeichen gelöscht.',
  missingBookmarksRemoved: 'Ungültige Lesezeichen entfernt.',
  bookmarksImportError: 'Lesezeichen konnten nicht importiert werden.',
  bookmarksExportError: 'Lesezeichen konnten nicht exportiert werden.',
  bookmarksLoadError: 'Gespeicherte Lesezeichen konnten nicht geladen werden.',
  bookmarksSaveError: 'Lesezeichen konnten nicht gespeichert werden. Ihre Änderungen wurden nicht übernommen.',
  invalidBookmarksCsv: 'Die Lesezeichen-CSV darf nur eine EVENT_ID-Spalte mit vier Hexadezimalzeichen pro ID enthalten.',
  pageHeading: 'AGT-Kalender für besondere Ereignisse',
  pageIntro: 'Durchsuche vergangene und kommende AGT-Treffen. Wähle ein Ereignis aus, um Zeitplan, Portaladresse und Beschreibung anzuzeigen.',
  syncHeading: 'Galaktisches Archiv wird synchronisiert',
  syncDescription: 'Eine aktuelle Kopie der Ereignisdatenbank wird abgerufen…',
  databaseError: 'Datenbankfehler',
  eventsCouldNotBeLoaded: 'Ereignisse konnten nicht geladen werden',
  databaseHttpError: 'Die Ereignisdatenbank hat HTTP {{status}} zurückgegeben.',
  unexpectedDatabaseError: 'Ein unerwarteter Datenbankfehler ist aufgetreten.',
  databaseInstructions: 'Prüfe die Verbindung und bestätige, dass die konfigurierte CSV veröffentlicht ist und Browserzugriff erlaubt.',
  retrySync: 'Synchronisierung erneut versuchen',
  databaseStatus: 'Datenbankstatus',
  validEventRecord: 'gültiger Ereigniseintrag',
  validEventRecords: 'gültige Ereigniseinträge',
  matchingFilters: 'passend zu den Filtern',
  lastSync: 'Letzte Synchronisierung',
  reloadDatabase: 'Datenbank neu laden',
  databaseLoadedWith: 'Datenbank mit {{count}} {{warningLabel}} geladen{{skipped}}',
  warning: 'Warnung',
  warnings: 'Warnungen',
  malformedRowsSkipped: '; {{count}} fehlerhafte Zeilen übersprungen',
  validEventsRemain: 'Gültige Ereignisse bleiben verfügbar. Zeilennummern beziehen sich auf die Quell-CSV und werden für Verantwortliche angezeigt.',
  rowLabel: 'Zeile {{row}}:',
  archiveEmpty: 'Archiv leer',
  noValidEvents: 'Keine gültigen Ereignisse verfügbar',
  emptyArchiveDescription: 'Die CSV wurde erfolgreich geladen, enthielt aber keine Zeilen, die dem erforderlichen Ereignisschema entsprachen.',
  home: 'Startseite',
  about: 'Über uns',
  team: 'Team',
  contribute: 'Mitwirken',
  galacticArchives: 'Galaktische Archive',
  engage: 'Mitmachen',
  terms: 'Bedingungen',
  support: 'Support',
  copyright: 'Urheberrecht',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'Lokale Zeit des Betrachters · {{timeZone}}',
  calendarNavigation: 'Kalendernavigation',
  previousMonth: 'Vorheriger Monat',
  today: 'Heute',
  nextMonth: 'Nächster Monat',
  goToMonth: 'Zum Monat wechseln',
  noMatchingEvents: 'Keine passenden Ereignisse in dieser Kalenderansicht.',
  matchingEventSummary: '{{count}} passendes Ereignis in dieser Kalenderansicht.',
  matchingEventsSummary: '{{count}} passende Ereignisse in dieser Kalenderansicht.',
  eventCalendar: 'Ereigniskalender',
  starts: 'Beginnt',
  continues: 'Wird fortgesetzt',
  ends: 'Endet',
  event: 'Ereignis',
  eventDetails: '{{name}}. {{context}}. Details öffnen.',
  eventDetailsAtTime: '{{name}}. {{context}} um {{time}}. Details öffnen.',
  archiveControls: 'Steuerung des galaktischen Archivs',
  filterEvents: 'Ereignisse filtern',
  clearAll: 'Alle löschen',
  clearAllCount: 'Alle löschen ({{count}})',
  search: 'Suchen',
  searchPlaceholder: 'Name, ID, Ort, Portalcode…',
  eventType: 'Ereignistyp',
  allEventTypes: 'Alle Ereignistypen',
  host: 'Gastgeber',
  allHosts: 'Alle Gastgeber',
  galaxy: 'Galaxie',
  allGalaxies: 'Alle Galaxien',
  dateContext: 'Zeitraum',
  allDates: 'Alle Daten',
  upcomingOnly: 'Nur bevorstehende',
  next30Days: 'Nächste 30 Tage',
  calendarPreferences: 'Kalendereinstellungen',
  closeSettings: 'Einstellungen schließen',
  changeLog: 'Änderungsprotokoll',
  closeChangeLog: 'Änderungsprotokoll schließen',
  loadingChangeLog: 'Änderungsprotokoll wird geladen…',
  changeLogLoadError: 'Das Änderungsprotokoll konnte nicht geladen werden.',
  noChangeLogEntries: 'Es sind keine Einträge im Änderungsprotokoll verfügbar.',
  fontScale: 'Schriftgröße',
  defaultLabel: 'Standard',
  fontScaleHelp: 'Skaliert die gesamte Kalenderoberfläche.',
  locale: 'Sprache und Region',
  localeHelp: 'Steuert lokalisierte Kalenderdaten, Uhrzeiten und Oberflächentexte.',
  localTimezone: 'Lokale Zeitzone',
  timeZoneHelp: 'Legt die Ereignisplatzierung und lokale Zeitplananzeige fest.',
  agtAnthem: 'AGT-Hymne',
  muteDefault: 'Stumm (Standard)',
  unmute: 'Ton einschalten',
  anthemHelp: 'Die Wiedergabe beginnt nach der ersten Interaktion.',
  reset: 'Zurücksetzen',
  import: 'Importieren',
  export: 'Exportieren',
  settingsExported: 'Einstellungen exportiert.',
  settingsExportError: 'Einstellungen konnten nicht exportiert werden.',
  settingsReset: 'Einstellungen zurückgesetzt.',
  settingsImported: 'Einstellungen importiert.',
  settingsImportError: 'Einstellungen konnten nicht importiert werden.',
  autoTimeZone: 'Automatisch',
  invalidSettingsJson: 'Die ausgewählte Datei enthält kein gültiges Einstellungs-JSON.',
  settingsJsonObject: 'Das Einstellungs-JSON muss ein Objekt enthalten.',
  unsupportedFontScale: 'Das Einstellungs-JSON enthält eine nicht unterstützte Schriftgröße.',
  unsupportedLocale: 'Das Einstellungs-JSON enthält eine nicht unterstützte Sprache.',
  unsupportedTimezone: 'Das Einstellungs-JSON enthält eine nicht unterstützte Zeitzone.',
  unsupportedAnthem: 'Das Einstellungs-JSON enthält eine nicht unterstützte Hymneneinstellung.',
  notSupplied: 'Nicht angegeben',
  eventRecord: 'Ereigniseintrag · {{id}}',
  eventUrlCopied: 'Ereignis-URL kopiert!',
  bookmarkAdded: 'Lesezeichen hinzugefügt!',
  bookmarkRemoved: 'Lesezeichen entfernt!',
  copyUrlError: 'URL konnte nicht kopiert werden',
  share: 'Teilen',
  closeEventDetails: 'Ereignisdetails schließen',
  eventCoverImage: 'Titelbild des Ereignisses',
  coverArtworkFor: 'Titelbild für {{name}}',
  noEventCover: 'Kein Ereignistitelbild verfügbar',
  coverLoadError: 'Titelbild konnte nicht geladen werden',
  noCoverSupplied: 'Kein Titelbild angegeben',
  schedule: 'Zeitplan',
  startLocal: 'Beginn · lokal',
  startUtc: 'Beginn · UTC',
  endLocal: 'Ende · lokal',
  endUtc: 'Ende · UTC',
  eventInformation: 'Ereignisinformationen',
  eventId: 'Ereignis-ID',
  region: 'Region',
  system: 'System',
  planet: 'Planet',
  portalCode: 'Portalcode',
  openGlyphTool: 'Mit Glyph Tool öffnen',
  description: 'Beschreibung',
  noMarkdownDescription: 'Keine Markdown-Beschreibung angegeben.',
  loadingRemoteDescription: 'Externe Beschreibung wird geladen…',
  remoteDescriptionError: 'Die externe Beschreibung konnte nicht geladen werden. Die obigen Ereignisdetails sind weiterhin verfügbar.',
  relatedLinks: 'Zugehörige Links',
  noRelatedLinks: 'Keine zugehörigen Links angegeben.',
  linkLabel: 'Link {{number}}',
  fieldRequired: '{{field}} ist erforderlich',
  eventIdHex: 'EVENT_ID muss genau vier Hexadezimalzeichen enthalten',
  portalCodeLength: 'EVENT_PORTAL_CODE muss genau 12 alphanumerische Zeichen enthalten',
  startUtcTimestamp: 'EVENT_DATETIME_START muss ein gültiger UTC-ISO-8601-Zeitstempel sein',
  endUtcTimestamp: 'EVENT_DATETIME_END muss ein gültiger UTC-ISO-8601-Zeitstempel sein',
  endBeforeStart: 'EVENT_DATETIME_END darf nicht vor EVENT_DATETIME_START liegen',
  unsafeDescriptionUrl: 'EVENT_MD_URI wurde ignoriert, da es keine sichere HTTP(S)-URL ist',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI wurde ignoriert, da es keine sichere HTTP(S)-URL ist',
  invalidRelatedLink: 'Zugehöriger Link wurde ignoriert, da er ungültig ist: {{value}}',
  csvParser: 'CSV-Parser: {{message}}',
  missingHeaders: 'Fehlende erforderliche CSV-Kopfzeilen: {{headers}}',
  duplicateEventId: 'Doppelte EVENT_ID {{id}}; Zeile wurde übersprungen',
};

const spanish: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'Ir al calendario',
  syncingDatabase: 'Sincronizando la base de datos',
  databaseOffline: 'Base de datos sin conexión',
  databaseLive: 'Base de datos en línea',
  openSettings: 'Abrir configuración',
  settings: 'Configuración',
  contactSupport: 'Contactar con soporte de AGT',
  addEvent: 'Añadir un evento',
  bookmarks: 'Marcadores',
  bookmarked: 'Marcados',
  bookmark: 'Añadir marcador',
  removeBookmark: 'Eliminar marcador',
  closeBookmarks: 'Cerrar marcadores',
  bookmarksBackupNotice: 'Los marcadores se guardan solo en este navegador, no en la nube. Pueden perderse si borras los datos de navegación. Usa Exportar para crear una copia de seguridad e Importar para restaurarlos.',
  noBookmarks: 'Aún no hay eventos guardados.',
  noMatchingBookmarks: 'Ningún marcador coincide con tu búsqueda.',
  missingBookmarks: '{{count}} marcadores apuntan a eventos que ya no existen.',
  removeMissingBookmarks: 'Eliminar marcadores no válidos',
  bookmarksImported: 'Marcadores importados y combinados.',
  bookmarksExported: 'Marcadores exportados.',
  bookmarksCleared: 'Marcadores borrados.',
  missingBookmarksRemoved: 'Marcadores no válidos eliminados.',
  bookmarksImportError: 'No se pudieron importar los marcadores.',
  bookmarksExportError: 'No se pudieron exportar los marcadores.',
  bookmarksLoadError: 'No se pudieron cargar los marcadores guardados.',
  bookmarksSaveError: 'No se pudieron guardar los marcadores. Los cambios no se aplicaron.',
  invalidBookmarksCsv: 'El CSV de marcadores debe contener solo una columna EVENT_ID con cuatro caracteres hexadecimales por ID.',
  pageHeading: 'Calendario de eventos especiales de AGT',
  pageIntro: 'Busca y explora reuniones de AGT pasadas y próximas. Selecciona un evento para ver su horario, dirección del portal y descripción.',
  syncHeading: 'Sincronizando el archivo galáctico',
  syncDescription: 'Obteniendo una copia reciente de la base de datos de eventos…',
  databaseError: 'Error de base de datos',
  eventsCouldNotBeLoaded: 'No se pudieron cargar los eventos',
  databaseHttpError: 'La base de datos de eventos devolvió HTTP {{status}}.',
  unexpectedDatabaseError: 'Se produjo un error inesperado en la base de datos.',
  databaseInstructions: 'Comprueba la conexión y confirma que el CSV configurado está publicado y permite el acceso desde el navegador.',
  retrySync: 'Reintentar sincronización',
  databaseStatus: 'Estado de la base de datos',
  validEventRecord: 'registro de evento válido',
  validEventRecords: 'registros de eventos válidos',
  matchingFilters: 'que coinciden con los filtros',
  lastSync: 'Última sincronización',
  reloadDatabase: 'Recargar base de datos',
  databaseLoadedWith: 'Base de datos cargada con {{count}} {{warningLabel}}{{skipped}}',
  warning: 'advertencia',
  warnings: 'advertencias',
  malformedRowsSkipped: '; se omitieron {{count}} filas con formato incorrecto',
  validEventsRemain: 'Los eventos válidos siguen disponibles. Los números de fila se refieren al CSV de origen y se muestran para los responsables.',
  rowLabel: 'Fila {{row}}:',
  archiveEmpty: 'Archivo vacío',
  noValidEvents: 'No hay eventos válidos disponibles',
  emptyArchiveDescription: 'El CSV se cargó correctamente, pero no contenía filas que cumplieran el esquema de evento requerido.',
  home: 'Inicio',
  about: 'Acerca de',
  team: 'Equipo',
  contribute: 'Contribuir',
  galacticArchives: 'Archivos galácticos',
  engage: 'Participar',
  terms: 'Términos',
  support: 'Soporte',
  copyright: 'Derechos de autor',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'Hora local del visitante · {{timeZone}}',
  calendarNavigation: 'Navegación del calendario',
  previousMonth: 'Mes anterior',
  today: 'Hoy',
  nextMonth: 'Mes siguiente',
  goToMonth: 'Ir al mes',
  noMatchingEvents: 'No hay eventos coincidentes en esta vista del calendario.',
  matchingEventSummary: '{{count}} evento coincidente en esta vista del calendario.',
  matchingEventsSummary: '{{count}} eventos coincidentes en esta vista del calendario.',
  eventCalendar: 'Calendario de eventos',
  starts: 'Comienza',
  continues: 'Continúa',
  ends: 'Termina',
  event: 'Evento',
  eventDetails: '{{name}}. {{context}}. Abrir detalles.',
  eventDetailsAtTime: '{{name}}. {{context}} a las {{time}}. Abrir detalles.',
  archiveControls: 'Controles del archivo galáctico',
  filterEvents: 'Filtrar eventos',
  clearAll: 'Borrar todo',
  clearAllCount: 'Borrar todo ({{count}})',
  search: 'Buscar',
  searchPlaceholder: 'Nombre, ID, ubicación, código de portal…',
  eventType: 'Tipo de evento',
  allEventTypes: 'Todos los tipos de evento',
  host: 'Anfitrión',
  allHosts: 'Todos los anfitriones',
  galaxy: 'Galaxia',
  allGalaxies: 'Todas las galaxias',
  dateContext: 'Periodo',
  allDates: 'Todas las fechas',
  upcomingOnly: 'Solo próximos',
  next30Days: 'Próximos 30 días',
  calendarPreferences: 'Preferencias del calendario',
  closeSettings: 'Cerrar configuración',
  changeLog: 'Registro de cambios',
  closeChangeLog: 'Cerrar el registro de cambios',
  loadingChangeLog: 'Cargando el registro de cambios…',
  changeLogLoadError: 'No se pudo cargar el registro de cambios.',
  noChangeLogEntries: 'No hay entradas disponibles en el registro de cambios.',
  fontScale: 'Escala de fuente',
  defaultLabel: 'Predeterminado',
  fontScaleHelp: 'Ajusta el tamaño de toda la interfaz del calendario.',
  locale: 'Idioma y región',
  localeHelp: 'Controla las fechas, horas y textos localizados de la interfaz.',
  localTimezone: 'Zona horaria local',
  timeZoneHelp: 'Define la ubicación de los eventos y la visualización de horarios locales.',
  agtAnthem: 'Himno de AGT',
  muteDefault: 'Silenciar (predeterminado)',
  unmute: 'Activar sonido',
  anthemHelp: 'La reproducción comienza después de tu primera interacción.',
  reset: 'Restablecer',
  import: 'Importar',
  export: 'Exportar',
  settingsExported: 'Configuración exportada.',
  settingsExportError: 'No se pudo exportar la configuración.',
  settingsReset: 'Configuración restablecida.',
  settingsImported: 'Configuración importada.',
  settingsImportError: 'No se pudo importar la configuración.',
  autoTimeZone: 'Automática',
  invalidSettingsJson: 'El archivo seleccionado no contiene un JSON de configuración válido.',
  settingsJsonObject: 'El JSON de configuración debe contener un objeto.',
  unsupportedFontScale: 'El JSON de configuración contiene una escala de fuente no compatible.',
  unsupportedLocale: 'El JSON de configuración contiene un idioma no compatible.',
  unsupportedTimezone: 'El JSON de configuración contiene una zona horaria no compatible.',
  unsupportedAnthem: 'El JSON de configuración contiene un ajuste de himno no compatible.',
  notSupplied: 'No proporcionado',
  eventRecord: 'Registro de evento · {{id}}',
  eventUrlCopied: '¡URL del evento copiada!',
  bookmarkAdded: '¡Marcador añadido!',
  bookmarkRemoved: '¡Marcador eliminado!',
  copyUrlError: 'No se pudo copiar la URL',
  share: 'Compartir',
  closeEventDetails: 'Cerrar detalles del evento',
  eventCoverImage: 'Imagen de portada del evento',
  coverArtworkFor: 'Portada de {{name}}',
  noEventCover: 'No hay portada del evento disponible',
  coverLoadError: 'No se pudo cargar la imagen de portada',
  noCoverSupplied: 'No se proporcionó una imagen de portada',
  schedule: 'Horario',
  startLocal: 'Inicio · local',
  startUtc: 'Inicio · UTC',
  endLocal: 'Fin · local',
  endUtc: 'Fin · UTC',
  eventInformation: 'Información del evento',
  eventId: 'ID del evento',
  region: 'Región',
  system: 'Sistema',
  planet: 'Planeta',
  portalCode: 'Código de portal',
  openGlyphTool: 'Abrir con Glyph Tool',
  description: 'Descripción',
  noMarkdownDescription: 'No se proporcionó una descripción Markdown.',
  loadingRemoteDescription: 'Cargando descripción remota…',
  remoteDescriptionError: 'No se pudo cargar la descripción remota. Los detalles del evento anteriores siguen disponibles.',
  relatedLinks: 'Enlaces relacionados',
  noRelatedLinks: 'No se proporcionaron enlaces relacionados.',
  linkLabel: 'Enlace {{number}}',
  fieldRequired: '{{field}} es obligatorio',
  eventIdHex: 'EVENT_ID debe contener exactamente cuatro caracteres hexadecimales',
  portalCodeLength: 'EVENT_PORTAL_CODE debe contener exactamente 12 caracteres alfanuméricos',
  startUtcTimestamp: 'EVENT_DATETIME_START debe ser una marca de tiempo UTC ISO 8601 válida',
  endUtcTimestamp: 'EVENT_DATETIME_END debe ser una marca de tiempo UTC ISO 8601 válida',
  endBeforeStart: 'EVENT_DATETIME_END no puede ser anterior a EVENT_DATETIME_START',
  unsafeDescriptionUrl: 'EVENT_MD_URI se ignoró porque no es una URL HTTP(S) segura',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI se ignoró porque no es una URL HTTP(S) segura',
  invalidRelatedLink: 'El enlace relacionado se ignoró porque no es válido: {{value}}',
  csvParser: 'Analizador CSV: {{message}}',
  missingHeaders: 'Faltan encabezados CSV obligatorios: {{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} duplicado; se omitió la fila',
};

const portuguese: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'Ir para o calendário',
  syncingDatabase: 'Sincronizando banco de dados',
  databaseOffline: 'Banco de dados offline',
  databaseLive: 'Banco de dados online',
  openSettings: 'Abrir configurações',
  settings: 'Configurações',
  contactSupport: 'Contatar o suporte da AGT',
  addEvent: 'Adicionar um evento',
  bookmarks: 'Favoritos',
  bookmarked: 'Favoritos',
  bookmark: 'Adicionar aos favoritos',
  removeBookmark: 'Remover dos favoritos',
  closeBookmarks: 'Fechar favoritos',
  bookmarksBackupNotice: 'Os favoritos são armazenados apenas neste navegador, não na nuvem. Eles podem ser perdidos se você limpar os dados de navegação. Use Exportar para fazer um backup e Importar para restaurá-los.',
  noBookmarks: 'Nenhum evento salvo ainda.',
  noMatchingBookmarks: 'Nenhum favorito corresponde à sua busca.',
  missingBookmarks: '{{count}} favoritos fazem referência a eventos que não existem mais.',
  removeMissingBookmarks: 'Remover favoritos inválidos',
  bookmarksImported: 'Favoritos importados e mesclados.',
  bookmarksExported: 'Favoritos exportados.',
  bookmarksCleared: 'Favoritos apagados.',
  missingBookmarksRemoved: 'Favoritos inválidos removidos.',
  bookmarksImportError: 'Não foi possível importar os favoritos.',
  bookmarksExportError: 'Não foi possível exportar os favoritos.',
  bookmarksLoadError: 'Não foi possível carregar os favoritos salvos.',
  bookmarksSaveError: 'Não foi possível salvar os favoritos. Suas alterações não foram aplicadas.',
  invalidBookmarksCsv: 'O CSV de favoritos deve conter apenas uma coluna EVENT_ID com quatro caracteres hexadecimais por ID.',
  pageHeading: 'Calendário de eventos especiais da AGT',
  pageIntro: 'Pesquise e explore encontros passados e futuros da AGT. Selecione um evento para ver sua programação, endereço do portal e descrição.',
  syncHeading: 'Sincronizando o arquivo galáctico',
  syncDescription: 'Buscando uma cópia atualizada do banco de dados de eventos…',
  databaseError: 'Erro no banco de dados',
  eventsCouldNotBeLoaded: 'Não foi possível carregar os eventos',
  databaseHttpError: 'O banco de dados de eventos retornou HTTP {{status}}.',
  unexpectedDatabaseError: 'Ocorreu um erro inesperado no banco de dados.',
  databaseInstructions: 'Verifique a conexão e confirme que o CSV configurado está publicado e permite acesso pelo navegador.',
  retrySync: 'Tentar sincronizar novamente',
  databaseStatus: 'Status do banco de dados',
  validEventRecord: 'registro de evento válido',
  validEventRecords: 'registros de eventos válidos',
  matchingFilters: 'correspondentes aos filtros',
  lastSync: 'Última sincronização',
  reloadDatabase: 'Recarregar banco de dados',
  databaseLoadedWith: 'Banco de dados carregado com {{count}} {{warningLabel}}{{skipped}}',
  warning: 'aviso',
  warnings: 'avisos',
  malformedRowsSkipped: '; {{count}} linhas malformadas ignoradas',
  validEventsRemain: 'Os eventos válidos continuam disponíveis. Os números das linhas se referem ao CSV de origem e são exibidos para os responsáveis.',
  rowLabel: 'Linha {{row}}:',
  archiveEmpty: 'Arquivo vazio',
  noValidEvents: 'Nenhum evento válido está disponível',
  emptyArchiveDescription: 'O CSV foi carregado, mas não continha linhas que atendessem ao esquema de evento exigido.',
  home: 'Início',
  about: 'Sobre',
  team: 'Equipe',
  contribute: 'Contribuir',
  galacticArchives: 'Arquivos galácticos',
  engage: 'Participar',
  terms: 'Termos',
  support: 'Suporte',
  copyright: 'Direitos autorais',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'Hora local do visitante · {{timeZone}}',
  calendarNavigation: 'Navegação do calendário',
  previousMonth: 'Mês anterior',
  today: 'Hoje',
  nextMonth: 'Próximo mês',
  goToMonth: 'Ir para o mês',
  noMatchingEvents: 'Nenhum evento correspondente nesta visualização do calendário.',
  matchingEventSummary: '{{count}} evento correspondente nesta visualização do calendário.',
  matchingEventsSummary: '{{count}} eventos correspondentes nesta visualização do calendário.',
  eventCalendar: 'Calendário de eventos',
  starts: 'Começa',
  continues: 'Continua',
  ends: 'Termina',
  event: 'Evento',
  eventDetails: '{{name}}. {{context}}. Abrir detalhes.',
  eventDetailsAtTime: '{{name}}. {{context}} às {{time}}. Abrir detalhes.',
  archiveControls: 'Controles do arquivo galáctico',
  filterEvents: 'Filtrar eventos',
  clearAll: 'Limpar tudo',
  clearAllCount: 'Limpar tudo ({{count}})',
  search: 'Pesquisar',
  searchPlaceholder: 'Nome, ID, local, código do portal…',
  eventType: 'Tipo de evento',
  allEventTypes: 'Todos os tipos de evento',
  host: 'Anfitrião',
  allHosts: 'Todos os anfitriões',
  galaxy: 'Galáxia',
  allGalaxies: 'Todas as galáxias',
  dateContext: 'Período',
  allDates: 'Todas as datas',
  upcomingOnly: 'Somente futuros',
  next30Days: 'Próximos 30 dias',
  calendarPreferences: 'Preferências do calendário',
  closeSettings: 'Fechar configurações',
  changeLog: 'Registro de alterações',
  closeChangeLog: 'Fechar o registro de alterações',
  loadingChangeLog: 'Carregando o registro de alterações…',
  changeLogLoadError: 'Não foi possível carregar o registro de alterações.',
  noChangeLogEntries: 'Não há entradas disponíveis no registro de alterações.',
  fontScale: 'Escala da fonte',
  defaultLabel: 'Padrão',
  fontScaleHelp: 'Redimensiona toda a interface do calendário.',
  locale: 'Idioma e região',
  localeHelp: 'Controla datas, horários e textos localizados da interface.',
  localTimezone: 'Fuso horário local',
  timeZoneHelp: 'Define o posicionamento dos eventos e a exibição dos horários locais.',
  agtAnthem: 'Hino da AGT',
  muteDefault: 'Silenciar (padrão)',
  unmute: 'Ativar som',
  anthemHelp: 'A reprodução começa após sua primeira interação.',
  reset: 'Redefinir',
  import: 'Importar',
  export: 'Exportar',
  settingsExported: 'Configurações exportadas.',
  settingsExportError: 'Não foi possível exportar as configurações.',
  settingsReset: 'Configurações redefinidas.',
  settingsImported: 'Configurações importadas.',
  settingsImportError: 'Não foi possível importar as configurações.',
  autoTimeZone: 'Automático',
  invalidSettingsJson: 'O arquivo selecionado não contém um JSON de configurações válido.',
  settingsJsonObject: 'O JSON de configurações deve conter um objeto.',
  unsupportedFontScale: 'O JSON de configurações contém uma escala de fonte não compatível.',
  unsupportedLocale: 'O JSON de configurações contém um idioma não compatível.',
  unsupportedTimezone: 'O JSON de configurações contém um fuso horário não compatível.',
  unsupportedAnthem: 'O JSON de configurações contém uma opção de hino não compatível.',
  notSupplied: 'Não informado',
  eventRecord: 'Registro do evento · {{id}}',
  eventUrlCopied: 'URL do evento copiada!',
  bookmarkAdded: 'Favorito adicionado!',
  bookmarkRemoved: 'Favorito removido!',
  copyUrlError: 'Não foi possível copiar a URL',
  share: 'Compartilhar',
  closeEventDetails: 'Fechar detalhes do evento',
  eventCoverImage: 'Imagem de capa do evento',
  coverArtworkFor: 'Arte de capa de {{name}}',
  noEventCover: 'Nenhuma capa de evento disponível',
  coverLoadError: 'Não foi possível carregar a imagem de capa',
  noCoverSupplied: 'Nenhuma imagem de capa fornecida',
  schedule: 'Programação',
  startLocal: 'Início · local',
  startUtc: 'Início · UTC',
  endLocal: 'Fim · local',
  endUtc: 'Fim · UTC',
  eventInformation: 'Informações do evento',
  eventId: 'ID do evento',
  region: 'Região',
  system: 'Sistema',
  planet: 'Planeta',
  portalCode: 'Código do portal',
  openGlyphTool: 'Abrir com Glyph Tool',
  description: 'Descrição',
  noMarkdownDescription: 'Nenhuma descrição Markdown fornecida.',
  loadingRemoteDescription: 'Carregando descrição remota…',
  remoteDescriptionError: 'Não foi possível carregar a descrição remota. Os detalhes do evento acima continuam disponíveis.',
  relatedLinks: 'Links relacionados',
  noRelatedLinks: 'Nenhum link relacionado fornecido.',
  linkLabel: 'Link {{number}}',
  fieldRequired: '{{field}} é obrigatório',
  eventIdHex: 'EVENT_ID deve conter exatamente quatro caracteres hexadecimais',
  portalCodeLength: 'EVENT_PORTAL_CODE deve conter exatamente 12 caracteres alfanuméricos',
  startUtcTimestamp: 'EVENT_DATETIME_START deve ser um carimbo de data/hora UTC ISO 8601 válido',
  endUtcTimestamp: 'EVENT_DATETIME_END deve ser um carimbo de data/hora UTC ISO 8601 válido',
  endBeforeStart: 'EVENT_DATETIME_END não pode ser anterior a EVENT_DATETIME_START',
  unsafeDescriptionUrl: 'EVENT_MD_URI foi ignorado porque não é uma URL HTTP(S) segura',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI foi ignorado porque não é uma URL HTTP(S) segura',
  invalidRelatedLink: 'O link relacionado foi ignorado porque é inválido: {{value}}',
  csvParser: 'Analisador CSV: {{message}}',
  missingHeaders: 'Cabeçalhos CSV obrigatórios ausentes: {{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} duplicado; a linha foi ignorada',
};

const italian: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'Vai al calendario',
  syncingDatabase: 'Sincronizzazione del database',
  databaseOffline: 'Database non in linea',
  databaseLive: 'Database in linea',
  openSettings: 'Apri impostazioni',
  settings: 'Impostazioni',
  contactSupport: 'Contatta il supporto AGT',
  addEvent: 'Aggiungi un evento',
  bookmarks: 'Preferiti',
  bookmarked: 'Preferiti',
  bookmark: 'Aggiungi ai preferiti',
  removeBookmark: 'Rimuovi dai preferiti',
  closeBookmarks: 'Chiudi preferiti',
  bookmarksBackupNotice: 'I preferiti sono salvati solo in questo browser, non nel cloud. Potrebbero andare persi se cancelli i dati di navigazione. Usa Esporta per crearne una copia di backup e Importa per ripristinarli.',
  noBookmarks: 'Nessun evento salvato.',
  noMatchingBookmarks: 'Nessun preferito corrisponde alla ricerca.',
  missingBookmarks: '{{count}} preferiti fanno riferimento a eventi che non esistono più.',
  removeMissingBookmarks: 'Rimuovi preferiti non validi',
  bookmarksImported: 'Preferiti importati e uniti.',
  bookmarksExported: 'Preferiti esportati.',
  bookmarksCleared: 'Preferiti cancellati.',
  missingBookmarksRemoved: 'Preferiti non validi rimossi.',
  bookmarksImportError: 'Impossibile importare i preferiti.',
  bookmarksExportError: 'Impossibile esportare i preferiti.',
  bookmarksLoadError: 'Impossibile caricare i preferiti salvati.',
  bookmarksSaveError: 'Impossibile salvare i preferiti. Le modifiche non sono state applicate.',
  invalidBookmarksCsv: 'Il CSV dei preferiti deve contenere solo una colonna EVENT_ID con quattro caratteri esadecimali per ID.',
  pageHeading: 'Calendario degli eventi speciali AGT',
  pageIntro: 'Cerca e sfoglia gli incontri AGT passati e futuri. Seleziona un evento per visualizzarne programma, indirizzo del portale e descrizione.',
  syncHeading: 'Sincronizzazione dell’archivio galattico',
  syncDescription: 'Recupero di una copia aggiornata del database degli eventi…',
  databaseError: 'Errore del database',
  eventsCouldNotBeLoaded: 'Impossibile caricare gli eventi',
  databaseHttpError: 'Il database degli eventi ha restituito HTTP {{status}}.',
  unexpectedDatabaseError: 'Si è verificato un errore imprevisto del database.',
  databaseInstructions: 'Controlla la connessione e verifica che il CSV configurato sia pubblicato e consenta l’accesso dal browser.',
  retrySync: 'Riprova sincronizzazione',
  databaseStatus: 'Stato del database',
  validEventRecord: 'evento valido',
  validEventRecords: 'eventi validi',
  matchingFilters: 'corrispondenti ai filtri',
  lastSync: 'Ultima sincronizzazione',
  reloadDatabase: 'Ricarica database',
  databaseLoadedWith: 'Database caricato con {{count}} {{warningLabel}}{{skipped}}',
  warning: 'avviso',
  warnings: 'avvisi',
  malformedRowsSkipped: '; {{count}} righe non valide ignorate',
  validEventsRemain: 'Gli eventi validi restano disponibili. I numeri di riga fanno riferimento al CSV di origine e sono mostrati ai responsabili.',
  rowLabel: 'Riga {{row}}:',
  archiveEmpty: 'Archivio vuoto',
  noValidEvents: 'Nessun evento valido disponibile',
  emptyArchiveDescription: 'Il CSV è stato caricato correttamente, ma non conteneva righe conformi allo schema richiesto.',
  home: 'Home',
  about: 'Informazioni',
  team: 'Squadra',
  contribute: 'Contribuisci',
  galacticArchives: 'Archivi galattici',
  engage: 'Partecipa',
  terms: 'Termini',
  support: 'Supporto',
  copyright: 'Copyright',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'Ora locale del visitatore · {{timeZone}}',
  calendarNavigation: 'Navigazione del calendario',
  previousMonth: 'Mese precedente',
  today: 'Oggi',
  nextMonth: 'Mese successivo',
  goToMonth: 'Vai al mese',
  noMatchingEvents: 'Nessun evento corrispondente in questa vista del calendario.',
  matchingEventSummary: '{{count}} evento corrispondente in questa vista del calendario.',
  matchingEventsSummary: '{{count}} eventi corrispondenti in questa vista del calendario.',
  eventCalendar: 'Calendario eventi',
  starts: 'Inizia',
  continues: 'Continua',
  ends: 'Termina',
  event: 'Evento',
  eventDetails: '{{name}}. {{context}}. Apri dettagli.',
  eventDetailsAtTime: '{{name}}. {{context}} alle {{time}}. Apri dettagli.',
  archiveControls: 'Controlli dell’archivio galattico',
  filterEvents: 'Filtra eventi',
  clearAll: 'Cancella tutto',
  clearAllCount: 'Cancella tutto ({{count}})',
  search: 'Cerca',
  searchPlaceholder: 'Nome, ID, luogo, codice portale…',
  eventType: 'Tipo di evento',
  allEventTypes: 'Tutti i tipi di evento',
  host: 'Organizzatore',
  allHosts: 'Tutti gli organizzatori',
  galaxy: 'Galassia',
  allGalaxies: 'Tutte le galassie',
  dateContext: 'Periodo',
  allDates: 'Tutte le date',
  upcomingOnly: 'Solo futuri',
  next30Days: 'Prossimi 30 giorni',
  calendarPreferences: 'Preferenze del calendario',
  closeSettings: 'Chiudi impostazioni',
  changeLog: 'Registro delle modifiche',
  closeChangeLog: 'Chiudi il registro delle modifiche',
  loadingChangeLog: 'Caricamento del registro delle modifiche…',
  changeLogLoadError: 'Impossibile caricare il registro delle modifiche.',
  noChangeLogEntries: 'Non sono disponibili voci nel registro delle modifiche.',
  fontScale: 'Scala carattere',
  defaultLabel: 'Predefinito',
  fontScaleHelp: 'Ridimensiona l’intera interfaccia del calendario.',
  locale: 'Lingua e area geografica',
  localeHelp: 'Controlla date, orari e testi localizzati dell’interfaccia.',
  localTimezone: 'Fuso orario locale',
  timeZoneHelp: 'Imposta il posizionamento degli eventi e gli orari locali.',
  agtAnthem: 'Inno AGT',
  muteDefault: 'Disattiva audio (predefinito)',
  unmute: 'Attiva audio',
  anthemHelp: 'La riproduzione inizia dopo la prima interazione.',
  reset: 'Ripristina',
  import: 'Importa',
  export: 'Esporta',
  settingsExported: 'Impostazioni esportate.',
  settingsExportError: 'Impossibile esportare le impostazioni.',
  settingsReset: 'Impostazioni ripristinate.',
  settingsImported: 'Impostazioni importate.',
  settingsImportError: 'Impossibile importare le impostazioni.',
  autoTimeZone: 'Automatico',
  invalidSettingsJson: 'Il file selezionato non contiene un JSON di impostazioni valido.',
  settingsJsonObject: 'Il JSON delle impostazioni deve contenere un oggetto.',
  unsupportedFontScale: 'Il JSON delle impostazioni contiene una scala del carattere non supportata.',
  unsupportedLocale: 'Il JSON delle impostazioni contiene una lingua non supportata.',
  unsupportedTimezone: 'Il JSON delle impostazioni contiene un fuso orario non supportato.',
  unsupportedAnthem: 'Il JSON delle impostazioni contiene un’opzione dell’inno non supportata.',
  notSupplied: 'Non fornito',
  eventRecord: 'Scheda evento · {{id}}',
  eventUrlCopied: 'URL dell’evento copiato!',
  bookmarkAdded: 'Segnalibro aggiunto!',
  bookmarkRemoved: 'Segnalibro rimosso!',
  copyUrlError: 'Impossibile copiare l’URL',
  share: 'Condividi',
  closeEventDetails: 'Chiudi dettagli evento',
  eventCoverImage: 'Immagine di copertina dell’evento',
  coverArtworkFor: 'Copertina di {{name}}',
  noEventCover: 'Nessuna copertina disponibile',
  coverLoadError: 'Impossibile caricare l’immagine di copertina',
  noCoverSupplied: 'Nessuna immagine di copertina fornita',
  schedule: 'Programma',
  startLocal: 'Inizio · locale',
  startUtc: 'Inizio · UTC',
  endLocal: 'Fine · locale',
  endUtc: 'Fine · UTC',
  eventInformation: 'Informazioni sull’evento',
  eventId: 'ID evento',
  region: 'Regione',
  system: 'Sistema',
  planet: 'Pianeta',
  portalCode: 'Codice portale',
  openGlyphTool: 'Apri con Glyph Tool',
  description: 'Descrizione',
  noMarkdownDescription: 'Nessuna descrizione Markdown fornita.',
  loadingRemoteDescription: 'Caricamento della descrizione remota…',
  remoteDescriptionError: 'Impossibile caricare la descrizione remota. I dettagli dell’evento sopra sono ancora disponibili.',
  relatedLinks: 'Link correlati',
  noRelatedLinks: 'Nessun link correlato fornito.',
  linkLabel: 'Link {{number}}',
  fieldRequired: '{{field}} è obbligatorio',
  eventIdHex: 'EVENT_ID deve contenere esattamente quattro caratteri esadecimali',
  portalCodeLength: 'EVENT_PORTAL_CODE deve contenere esattamente 12 caratteri alfanumerici',
  startUtcTimestamp: 'EVENT_DATETIME_START deve essere un timestamp UTC ISO 8601 valido',
  endUtcTimestamp: 'EVENT_DATETIME_END deve essere un timestamp UTC ISO 8601 valido',
  endBeforeStart: 'EVENT_DATETIME_END non può precedere EVENT_DATETIME_START',
  unsafeDescriptionUrl: 'EVENT_MD_URI è stato ignorato perché non è un URL HTTP(S) sicuro',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI è stato ignorato perché non è un URL HTTP(S) sicuro',
  invalidRelatedLink: 'Il link correlato è stato ignorato perché non valido: {{value}}',
  csvParser: 'Parser CSV: {{message}}',
  missingHeaders: 'Intestazioni CSV obbligatorie mancanti: {{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} duplicato; riga ignorata',
};

const thai: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'ข้ามไปยังปฏิทิน',
  syncingDatabase: 'กำลังซิงค์ฐานข้อมูล',
  databaseOffline: 'ฐานข้อมูลออฟไลน์',
  databaseLive: 'ฐานข้อมูลออนไลน์',
  openSettings: 'เปิดการตั้งค่า',
  settings: 'การตั้งค่า',
  contactSupport: 'ติดต่อฝ่ายสนับสนุน AGT',
  addEvent: 'เพิ่มกิจกรรม',
  bookmarks: 'บุ๊กมาร์ก',
  bookmarked: 'บุ๊กมาร์กแล้ว',
  bookmark: 'เพิ่มบุ๊กมาร์ก',
  removeBookmark: 'ลบบุ๊กมาร์ก',
  closeBookmarks: 'ปิดบุ๊กมาร์ก',
  bookmarksBackupNotice: 'บุ๊กมาร์กจะถูกเก็บไว้ในเบราว์เซอร์นี้เท่านั้น ไม่ได้เก็บไว้บนคลาวด์ และอาจสูญหายหากคุณล้างข้อมูลการท่องเว็บ ใช้ปุ่มส่งออกเพื่อสำรองข้อมูลและปุ่มนำเข้าเพื่อกู้คืนบุ๊กมาร์ก',
  noBookmarks: 'ยังไม่มีกิจกรรมที่บันทึกไว้',
  noMatchingBookmarks: 'ไม่พบบุ๊กมาร์กที่ตรงกับการค้นหา',
  missingBookmarks: 'บุ๊กมาร์ก {{count}} รายการอ้างถึงกิจกรรมที่ไม่มีอยู่แล้ว',
  removeMissingBookmarks: 'ลบบุ๊กมาร์กที่ไม่ถูกต้อง',
  bookmarksImported: 'นำเข้าและรวมบุ๊กมาร์กแล้ว',
  bookmarksExported: 'ส่งออกบุ๊กมาร์กแล้ว',
  bookmarksCleared: 'ล้างบุ๊กมาร์กแล้ว',
  missingBookmarksRemoved: 'ลบบุ๊กมาร์กที่ไม่ถูกต้องแล้ว',
  bookmarksImportError: 'ไม่สามารถนำเข้าบุ๊กมาร์กได้',
  bookmarksExportError: 'ไม่สามารถส่งออกบุ๊กมาร์กได้',
  bookmarksLoadError: 'ไม่สามารถโหลดบุ๊กมาร์กที่บันทึกไว้ได้',
  bookmarksSaveError: 'ไม่สามารถบันทึกบุ๊กมาร์กได้ การเปลี่ยนแปลงยังไม่ได้ถูกนำไปใช้',
  invalidBookmarksCsv: 'ไฟล์ CSV ของบุ๊กมาร์กต้องมีเฉพาะคอลัมน์ EVENT_ID โดยแต่ละ ID ต้องเป็นอักขระฐานสิบหกสี่ตัว',
  pageHeading: 'ปฏิทินกิจกรรมพิเศษ AGT',
  pageIntro: 'ค้นหาและเรียกดูกิจกรรม AGT ทั้งที่ผ่านมาและที่กำลังจะมาถึง เลือกกิจกรรมเพื่อดูตารางเวลา ที่อยู่พอร์ทัล และคำอธิบาย',
  syncHeading: 'กำลังซิงค์คลังข้อมูลกาแล็กซี',
  syncDescription: 'กำลังดึงสำเนาล่าสุดของฐานข้อมูลกิจกรรม…',
  databaseError: 'ข้อผิดพลาดของฐานข้อมูล',
  eventsCouldNotBeLoaded: 'ไม่สามารถโหลดกิจกรรมได้',
  databaseHttpError: 'ฐานข้อมูลกิจกรรมส่งคืน HTTP {{status}}',
  unexpectedDatabaseError: 'เกิดข้อผิดพลาดที่ไม่คาดคิดกับฐานข้อมูล',
  databaseInstructions: 'ตรวจสอบการเชื่อมต่อและยืนยันว่า CSV ที่กำหนดค่าเผยแพร่แล้วและอนุญาตให้เบราว์เซอร์เข้าถึง',
  retrySync: 'ลองซิงค์อีกครั้ง',
  databaseStatus: 'สถานะฐานข้อมูล',
  validEventRecord: 'ระเบียนกิจกรรมที่ถูกต้อง',
  validEventRecords: 'ระเบียนกิจกรรมที่ถูกต้อง',
  matchingFilters: 'ตรงกับตัวกรอง',
  lastSync: 'ซิงค์ล่าสุด',
  reloadDatabase: 'โหลดฐานข้อมูลใหม่',
  databaseLoadedWith: 'โหลดฐานข้อมูลพร้อมคำเตือน {{count}} รายการ{{skipped}}',
  warning: 'คำเตือน',
  warnings: 'คำเตือน',
  malformedRowsSkipped: '; ข้ามแถวที่ผิดรูปแบบ {{count}} แถว',
  validEventsRemain: 'กิจกรรมที่ถูกต้องยังคงใช้งานได้ หมายเลขแถวอ้างอิง CSV ต้นทางและแสดงสำหรับผู้ดูแล',
  rowLabel: 'แถว {{row}}:',
  archiveEmpty: 'คลังข้อมูลว่าง',
  noValidEvents: 'ไม่มีกิจกรรมที่ถูกต้อง',
  emptyArchiveDescription: 'โหลด CSV สำเร็จแล้ว แต่ไม่มีแถวที่ตรงตามโครงสร้างกิจกรรมที่กำหนด',
  home: 'หน้าแรก',
  about: 'เกี่ยวกับ',
  team: 'ทีม',
  contribute: 'ร่วมสนับสนุน',
  galacticArchives: 'คลังข้อมูลกาแล็กซี',
  engage: 'มีส่วนร่วม',
  terms: 'ข้อกำหนด',
  support: 'ฝ่ายสนับสนุน',
  copyright: 'ลิขสิทธิ์',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'เวลาท้องถิ่นของผู้ดู · {{timeZone}}',
  calendarNavigation: 'การนำทางปฏิทิน',
  previousMonth: 'เดือนก่อนหน้า',
  today: 'วันนี้',
  nextMonth: 'เดือนถัดไป',
  goToMonth: 'ไปยังเดือน',
  noMatchingEvents: 'ไม่มีกิจกรรมที่ตรงกันในมุมมองปฏิทินนี้',
  matchingEventSummary: 'มีกิจกรรมที่ตรงกัน {{count}} รายการในมุมมองปฏิทินนี้',
  matchingEventsSummary: 'มีกิจกรรมที่ตรงกัน {{count}} รายการในมุมมองปฏิทินนี้',
  eventCalendar: 'ปฏิทินกิจกรรม',
  starts: 'เริ่ม',
  continues: 'ดำเนินต่อ',
  ends: 'สิ้นสุด',
  event: 'กิจกรรม',
  eventDetails: '{{name}} {{context}} เปิดรายละเอียด',
  eventDetailsAtTime: '{{name}} {{context}} เวลา {{time}} เปิดรายละเอียด',
  archiveControls: 'ตัวควบคุมคลังข้อมูลกาแล็กซี',
  filterEvents: 'กรองกิจกรรม',
  clearAll: 'ล้างทั้งหมด',
  clearAllCount: 'ล้างทั้งหมด ({{count}})',
  search: 'ค้นหา',
  searchPlaceholder: 'ชื่อ, ID, สถานที่, รหัสพอร์ทัล…',
  eventType: 'ประเภทกิจกรรม',
  allEventTypes: 'กิจกรรมทุกประเภท',
  host: 'ผู้จัด',
  allHosts: 'ผู้จัดทั้งหมด',
  galaxy: 'กาแล็กซี',
  allGalaxies: 'ทุกกาแล็กซี',
  dateContext: 'ช่วงวันที่',
  allDates: 'ทุกวันที่',
  upcomingOnly: 'ที่กำลังจะมาถึงเท่านั้น',
  next30Days: '30 วันถัดไป',
  calendarPreferences: 'การกำหนดลักษณะปฏิทิน',
  closeSettings: 'ปิดการตั้งค่า',
  changeLog: 'บันทึกการเปลี่ยนแปลง',
  closeChangeLog: 'ปิดบันทึกการเปลี่ยนแปลง',
  loadingChangeLog: 'กำลังโหลดบันทึกการเปลี่ยนแปลง…',
  changeLogLoadError: 'ไม่สามารถโหลดบันทึกการเปลี่ยนแปลงได้',
  noChangeLogEntries: 'ไม่มีรายการบันทึกการเปลี่ยนแปลง',
  fontScale: 'ขนาดตัวอักษร',
  defaultLabel: 'ค่าเริ่มต้น',
  fontScaleHelp: 'ปรับขนาดอินเทอร์เฟซปฏิทินทั้งหมด',
  locale: 'ภาษาและภูมิภาค',
  localeHelp: 'ควบคุมวันที่ เวลา และข้อความอินเทอร์เฟซที่แปลแล้ว',
  localTimezone: 'เขตเวลาท้องถิ่น',
  timeZoneHelp: 'กำหนดตำแหน่งกิจกรรมและการแสดงตารางเวลาท้องถิ่น',
  agtAnthem: 'เพลงประจำ AGT',
  muteDefault: 'ปิดเสียง (ค่าเริ่มต้น)',
  unmute: 'เปิดเสียง',
  anthemHelp: 'การเล่นจะเริ่มหลังจากการโต้ตอบครั้งแรก',
  reset: 'รีเซ็ต',
  import: 'นำเข้า',
  export: 'ส่งออก',
  settingsExported: 'ส่งออกการตั้งค่าแล้ว',
  settingsExportError: 'ไม่สามารถส่งออกการตั้งค่าได้',
  settingsReset: 'รีเซ็ตการตั้งค่าแล้ว',
  settingsImported: 'นำเข้าการตั้งค่าแล้ว',
  settingsImportError: 'ไม่สามารถนำเข้าการตั้งค่าได้',
  autoTimeZone: 'อัตโนมัติ',
  invalidSettingsJson: 'ไฟล์ที่เลือกไม่ใช่ JSON การตั้งค่าที่ถูกต้อง',
  settingsJsonObject: 'JSON การตั้งค่าต้องมีออบเจ็กต์',
  unsupportedFontScale: 'JSON การตั้งค่ามีขนาดตัวอักษรที่ไม่รองรับ',
  unsupportedLocale: 'JSON การตั้งค่ามีภาษาที่ไม่รองรับ',
  unsupportedTimezone: 'JSON การตั้งค่ามีเขตเวลาที่ไม่รองรับ',
  unsupportedAnthem: 'JSON การตั้งค่ามีตัวเลือกเพลงที่ไม่รองรับ',
  notSupplied: 'ไม่ได้ระบุ',
  eventRecord: 'ระเบียนกิจกรรม · {{id}}',
  eventUrlCopied: 'คัดลอก URL กิจกรรมแล้ว!',
  bookmarkAdded: 'เพิ่มบุ๊กมาร์กแล้ว!',
  bookmarkRemoved: 'ลบบุ๊กมาร์กแล้ว!',
  copyUrlError: 'ไม่สามารถคัดลอก URL ได้',
  share: 'แชร์',
  closeEventDetails: 'ปิดรายละเอียดกิจกรรม',
  eventCoverImage: 'ภาพปกกิจกรรม',
  coverArtworkFor: 'ภาพปกสำหรับ {{name}}',
  noEventCover: 'ไม่มีภาพปกกิจกรรม',
  coverLoadError: 'ไม่สามารถโหลดภาพปกได้',
  noCoverSupplied: 'ไม่ได้ระบุภาพปก',
  schedule: 'กำหนดการ',
  startLocal: 'เริ่ม · เวลาท้องถิ่น',
  startUtc: 'เริ่ม · UTC',
  endLocal: 'สิ้นสุด · เวลาท้องถิ่น',
  endUtc: 'สิ้นสุด · UTC',
  eventInformation: 'ข้อมูลกิจกรรม',
  eventId: 'ID กิจกรรม',
  region: 'ภูมิภาค',
  system: 'ระบบ',
  planet: 'ดาวเคราะห์',
  portalCode: 'รหัสพอร์ทัล',
  openGlyphTool: 'เปิดด้วย Glyph Tool',
  description: 'คำอธิบาย',
  noMarkdownDescription: 'ไม่ได้ระบุคำอธิบาย Markdown',
  loadingRemoteDescription: 'กำลังโหลดคำอธิบายระยะไกล…',
  remoteDescriptionError: 'ไม่สามารถโหลดคำอธิบายระยะไกลได้ รายละเอียดกิจกรรมด้านบนยังคงใช้งานได้',
  relatedLinks: 'ลิงก์ที่เกี่ยวข้อง',
  noRelatedLinks: 'ไม่ได้ระบุลิงก์ที่เกี่ยวข้อง',
  linkLabel: 'ลิงก์ {{number}}',
  fieldRequired: 'ต้องระบุ {{field}}',
  eventIdHex: 'EVENT_ID ต้องมีอักขระฐานสิบหกสี่ตัวพอดี',
  portalCodeLength: 'EVENT_PORTAL_CODE ต้องมีอักขระตัวอักษรและตัวเลข 12 ตัวพอดี',
  startUtcTimestamp: 'EVENT_DATETIME_START ต้องเป็นเวลาประทับ UTC ISO 8601 ที่ถูกต้อง',
  endUtcTimestamp: 'EVENT_DATETIME_END ต้องเป็นเวลาประทับ UTC ISO 8601 ที่ถูกต้อง',
  endBeforeStart: 'EVENT_DATETIME_END ต้องไม่อยู่ก่อน EVENT_DATETIME_START',
  unsafeDescriptionUrl: 'ละเว้น EVENT_MD_URI เนื่องจากไม่ใช่ URL HTTP(S) ที่ปลอดภัย',
  unsafeCoverUrl: 'ละเว้น EVENT_COVER_IMG_URI เนื่องจากไม่ใช่ URL HTTP(S) ที่ปลอดภัย',
  invalidRelatedLink: 'ละเว้นลิงก์ที่เกี่ยวข้องเนื่องจากไม่ถูกต้อง: {{value}}',
  csvParser: 'ตัวแยกวิเคราะห์ CSV: {{message}}',
  missingHeaders: 'ไม่มีส่วนหัว CSV ที่จำเป็น: {{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} ซ้ำ จึงข้ามแถวนี้',
};

const chinese: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: '跳转到日历',
  syncingDatabase: '正在同步数据库',
  databaseOffline: '数据库离线',
  databaseLive: '数据库在线',
  openSettings: '打开设置',
  settings: '设置',
  contactSupport: '联系 AGT 支持',
  addEvent: '添加活动',
  bookmarks: '书签',
  bookmarked: '已收藏',
  bookmark: '添加书签',
  removeBookmark: '移除书签',
  closeBookmarks: '关闭书签',
  bookmarksBackupNotice: '书签仅保存在此浏览器中，不会存储在云端。清除浏览数据可能导致书签丢失。使用“导出”备份书签，使用“导入”恢复书签。',
  noBookmarks: '尚未收藏任何活动。',
  noMatchingBookmarks: '没有符合搜索条件的书签。',
  missingBookmarks: '有 {{count}} 个书签指向已不存在的活动。',
  removeMissingBookmarks: '移除无效书签',
  bookmarksImported: '书签已导入并合并。',
  bookmarksExported: '书签已导出。',
  bookmarksCleared: '书签已清空。',
  missingBookmarksRemoved: '无效书签已移除。',
  bookmarksImportError: '无法导入书签。',
  bookmarksExportError: '无法导出书签。',
  bookmarksLoadError: '无法加载已保存的书签。',
  bookmarksSaveError: '无法保存书签。更改未应用。',
  invalidBookmarksCsv: '书签 CSV 只能包含 EVENT_ID 列，每个 ID 必须为四位十六进制字符。',
  pageHeading: 'AGT 特别活动日历',
  pageIntro: '搜索和浏览过去及即将举行的 AGT 聚会。选择任一活动可查看日程、传送门地址和说明。',
  syncHeading: '正在同步银河档案',
  syncDescription: '正在获取活动数据库的最新副本…',
  databaseError: '数据库错误',
  eventsCouldNotBeLoaded: '无法加载活动',
  databaseHttpError: '活动数据库返回了 HTTP {{status}}。',
  unexpectedDatabaseError: '发生了意外的数据库错误。',
  databaseInstructions: '请检查连接，并确认配置的 CSV 已发布且允许浏览器访问。',
  retrySync: '重试同步',
  databaseStatus: '数据库状态',
  validEventRecord: '条有效活动记录',
  validEventRecords: '条有效活动记录',
  matchingFilters: '条符合筛选条件',
  lastSync: '上次同步',
  reloadDatabase: '重新加载数据库',
  databaseLoadedWith: '数据库已加载，有 {{count}} 条{{warningLabel}}{{skipped}}',
  warning: '警告',
  warnings: '警告',
  malformedRowsSkipped: '；已跳过 {{count}} 个格式错误的行',
  validEventsRemain: '有效活动仍可使用。行号对应源 CSV，仅供维护人员参考。',
  rowLabel: '第 {{row}} 行：',
  archiveEmpty: '档案为空',
  noValidEvents: '没有可用的有效活动',
  emptyArchiveDescription: 'CSV 已成功加载，但其中没有符合所需活动结构的行。',
  home: '首页',
  about: '关于',
  team: '团队',
  contribute: '贡献',
  galacticArchives: '银河档案',
  engage: '参与',
  terms: '条款',
  support: '支持',
  copyright: '版权',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: '查看者当地时间 · {{timeZone}}',
  calendarNavigation: '日历导航',
  previousMonth: '上个月',
  today: '今天',
  nextMonth: '下个月',
  goToMonth: '转到月份',
  noMatchingEvents: '此日历视图中没有匹配的活动。',
  matchingEventSummary: '此日历视图中有 {{count}} 个匹配的活动。',
  matchingEventsSummary: '此日历视图中有 {{count}} 个匹配的活动。',
  eventCalendar: '活动日历',
  starts: '开始',
  continues: '持续',
  ends: '结束',
  event: '活动',
  eventDetails: '{{name}}。{{context}}。打开详情。',
  eventDetailsAtTime: '{{name}}。{{context}}于 {{time}}。打开详情。',
  archiveControls: '银河档案控件',
  filterEvents: '筛选活动',
  clearAll: '全部清除',
  clearAllCount: '全部清除（{{count}}）',
  search: '搜索',
  searchPlaceholder: '名称、ID、地点、传送门代码…',
  eventType: '活动类型',
  allEventTypes: '所有活动类型',
  host: '主办方',
  allHosts: '所有主办方',
  galaxy: '星系',
  allGalaxies: '所有星系',
  dateContext: '日期范围',
  allDates: '所有日期',
  upcomingOnly: '仅即将举行',
  next30Days: '未来 30 天',
  calendarPreferences: '日历偏好设置',
  closeSettings: '关闭设置',
  changeLog: '更新日志',
  closeChangeLog: '关闭更新日志',
  loadingChangeLog: '正在加载更新日志…',
  changeLogLoadError: '无法加载更新日志。',
  noChangeLogEntries: '暂无更新日志条目。',
  fontScale: '字体缩放',
  defaultLabel: '默认',
  fontScaleHelp: '缩放整个日历界面。',
  locale: '语言和地区',
  localeHelp: '控制本地化的日历日期、时间和界面文字。',
  localTimezone: '本地时区',
  timeZoneHelp: '设置活动位置和本地日程显示。',
  agtAnthem: 'AGT 会歌',
  muteDefault: '静音（默认）',
  unmute: '取消静音',
  anthemHelp: '首次互动后开始播放。',
  reset: '重置',
  import: '导入',
  export: '导出',
  settingsExported: '设置已导出。',
  settingsExportError: '无法导出设置。',
  settingsReset: '设置已重置。',
  settingsImported: '设置已导入。',
  settingsImportError: '无法导入设置。',
  autoTimeZone: '自动',
  invalidSettingsJson: '所选文件不是有效的设置 JSON。',
  settingsJsonObject: '设置 JSON 必须包含一个对象。',
  unsupportedFontScale: '设置 JSON 包含不支持的字体缩放。',
  unsupportedLocale: '设置 JSON 包含不支持的语言。',
  unsupportedTimezone: '设置 JSON 包含不支持的时区。',
  unsupportedAnthem: '设置 JSON 包含不支持的会歌选项。',
  notSupplied: '未提供',
  eventRecord: '活动记录 · {{id}}',
  eventUrlCopied: '活动 URL 已复制！',
  bookmarkAdded: '书签已添加！',
  bookmarkRemoved: '书签已移除！',
  copyUrlError: '无法复制 URL',
  share: '分享',
  closeEventDetails: '关闭活动详情',
  eventCoverImage: '活动封面图片',
  coverArtworkFor: '{{name}} 的封面图',
  noEventCover: '没有可用的活动封面',
  coverLoadError: '无法加载封面图片',
  noCoverSupplied: '未提供封面图片',
  schedule: '日程',
  startLocal: '开始 · 本地时间',
  startUtc: '开始 · UTC',
  endLocal: '结束 · 本地时间',
  endUtc: '结束 · UTC',
  eventInformation: '活动信息',
  eventId: '活动 ID',
  region: '区域',
  system: '恒星系',
  planet: '行星',
  portalCode: '传送门代码',
  openGlyphTool: '使用 Glyph Tool 打开',
  description: '说明',
  noMarkdownDescription: '未提供 Markdown 说明。',
  loadingRemoteDescription: '正在加载远程说明…',
  remoteDescriptionError: '无法加载远程说明。上方的活动详情仍然可用。',
  relatedLinks: '相关链接',
  noRelatedLinks: '未提供相关链接。',
  linkLabel: '链接 {{number}}',
  fieldRequired: '{{field}} 为必填项',
  eventIdHex: 'EVENT_ID 必须恰好包含四个十六进制字符',
  portalCodeLength: 'EVENT_PORTAL_CODE 必须恰好包含 12 个字母数字字符',
  startUtcTimestamp: 'EVENT_DATETIME_START 必须是有效的 UTC ISO 8601 时间戳',
  endUtcTimestamp: 'EVENT_DATETIME_END 必须是有效的 UTC ISO 8601 时间戳',
  endBeforeStart: 'EVENT_DATETIME_END 不能早于 EVENT_DATETIME_START',
  unsafeDescriptionUrl: 'EVENT_MD_URI 不是安全的 HTTP(S) URL，已忽略',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI 不是安全的 HTTP(S) URL，已忽略',
  invalidRelatedLink: '相关链接无效，已忽略：{{value}}',
  csvParser: 'CSV 解析器：{{message}}',
  missingHeaders: '缺少必需的 CSV 标头：{{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} 重复；已跳过该行',
};

const hindi: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'कैलेंडर पर जाएँ',
  syncingDatabase: 'डेटाबेस सिंक हो रहा है',
  databaseOffline: 'डेटाबेस ऑफ़लाइन है',
  databaseLive: 'डेटाबेस ऑनलाइन है',
  openSettings: 'सेटिंग खोलें',
  settings: 'सेटिंग',
  contactSupport: 'AGT सहायता से संपर्क करें',
  addEvent: 'कार्यक्रम जोड़ें',
  bookmarks: 'बुकमार्क',
  bookmarked: 'बुकमार्क किए गए',
  bookmark: 'बुकमार्क करें',
  removeBookmark: 'बुकमार्क हटाएँ',
  closeBookmarks: 'बुकमार्क बंद करें',
  bookmarksBackupNotice: 'बुकमार्क केवल इस ब्राउज़र में सहेजे जाते हैं, क्लाउड में नहीं। ब्राउज़िंग डेटा साफ़ करने पर वे खो सकते हैं। बैकअप बनाने के लिए निर्यात और उन्हें वापस लाने के लिए आयात का उपयोग करें।',
  noBookmarks: 'अभी कोई कार्यक्रम सहेजा नहीं गया है।',
  noMatchingBookmarks: 'आपकी खोज से मेल खाने वाला कोई बुकमार्क नहीं है।',
  missingBookmarks: '{{count}} बुकमार्क ऐसे कार्यक्रमों के हैं जो अब मौजूद नहीं हैं।',
  removeMissingBookmarks: 'अमान्य बुकमार्क हटाएँ',
  bookmarksImported: 'बुकमार्क आयात करके मिला दिए गए।',
  bookmarksExported: 'बुकमार्क निर्यात किए गए।',
  bookmarksCleared: 'बुकमार्क साफ़ किए गए।',
  missingBookmarksRemoved: 'अमान्य बुकमार्क हटाए गए।',
  bookmarksImportError: 'बुकमार्क आयात नहीं किए जा सके।',
  bookmarksExportError: 'बुकमार्क निर्यात नहीं किए जा सके।',
  bookmarksLoadError: 'सहेजे गए बुकमार्क लोड नहीं किए जा सके।',
  bookmarksSaveError: 'बुकमार्क सहेजे नहीं जा सके। आपके बदलाव लागू नहीं किए गए।',
  invalidBookmarksCsv: 'बुकमार्क CSV में केवल EVENT_ID कॉलम होना चाहिए और हर ID में चार हेक्साडेसिमल वर्ण होने चाहिए।',
  pageHeading: 'AGT विशेष कार्यक्रम कैलेंडर',
  pageIntro: 'पिछले और आगामी AGT आयोजनों को खोजें और देखें। समय-सारणी, पोर्टल पता और विवरण देखने के लिए कोई कार्यक्रम चुनें।',
  syncHeading: 'गैलेक्टिक अभिलेख सिंक हो रहा है',
  syncDescription: 'कार्यक्रम डेटाबेस की नई प्रति प्राप्त की जा रही है…',
  databaseError: 'डेटाबेस त्रुटि',
  eventsCouldNotBeLoaded: 'कार्यक्रम लोड नहीं किए जा सके',
  databaseHttpError: 'कार्यक्रम डेटाबेस ने HTTP {{status}} लौटाया।',
  unexpectedDatabaseError: 'डेटाबेस में अनपेक्षित त्रुटि हुई।',
  databaseInstructions: 'कनेक्शन जाँचें और पुष्टि करें कि कॉन्फ़िगर किया गया CSV प्रकाशित है और ब्राउज़र पहुँच की अनुमति देता है।',
  retrySync: 'फिर से सिंक करें',
  databaseStatus: 'डेटाबेस स्थिति',
  validEventRecord: 'मान्य कार्यक्रम रिकॉर्ड',
  validEventRecords: 'मान्य कार्यक्रम रिकॉर्ड',
  matchingFilters: 'फ़िल्टर से मेल खाते',
  lastSync: 'अंतिम सिंक',
  reloadDatabase: 'डेटाबेस फिर लोड करें',
  databaseLoadedWith: 'डेटाबेस {{count}} {{warningLabel}} के साथ लोड हुआ{{skipped}}',
  warning: 'चेतावनी',
  warnings: 'चेतावनियाँ',
  malformedRowsSkipped: '; {{count}} गलत स्वरूप वाली पंक्तियाँ छोड़ी गईं',
  validEventsRemain: 'मान्य कार्यक्रम उपलब्ध हैं। पंक्ति संख्याएँ स्रोत CSV को दर्शाती हैं और रखरखावकर्ताओं के लिए दिखाई जाती हैं।',
  rowLabel: 'पंक्ति {{row}}:',
  archiveEmpty: 'अभिलेख खाली है',
  noValidEvents: 'कोई मान्य कार्यक्रम उपलब्ध नहीं है',
  emptyArchiveDescription: 'CSV सफलतापूर्वक लोड हुआ, लेकिन उसमें आवश्यक कार्यक्रम संरचना से मेल खाने वाली कोई पंक्ति नहीं थी।',
  home: 'मुखपृष्ठ',
  about: 'परिचय',
  team: 'टीम',
  contribute: 'योगदान दें',
  galacticArchives: 'गैलेक्टिक अभिलेख',
  engage: 'भाग लें',
  terms: 'शर्तें',
  support: 'सहायता',
  copyright: 'कॉपीराइट',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: 'दर्शक का स्थानीय समय · {{timeZone}}',
  calendarNavigation: 'कैलेंडर नेविगेशन',
  previousMonth: 'पिछला महीना',
  today: 'आज',
  nextMonth: 'अगला महीना',
  goToMonth: 'महीने पर जाएँ',
  noMatchingEvents: 'इस कैलेंडर दृश्य में कोई मेल खाता कार्यक्रम नहीं है।',
  matchingEventSummary: 'इस कैलेंडर दृश्य में {{count}} मेल खाता कार्यक्रम है।',
  matchingEventsSummary: 'इस कैलेंडर दृश्य में {{count}} मेल खाते कार्यक्रम हैं।',
  eventCalendar: 'कार्यक्रम कैलेंडर',
  starts: 'आरंभ',
  continues: 'जारी',
  ends: 'समाप्त',
  event: 'कार्यक्रम',
  eventDetails: '{{name}}। {{context}}। विवरण खोलें।',
  eventDetailsAtTime: '{{name}}। {{context}}, {{time}} बजे। विवरण खोलें।',
  archiveControls: 'गैलेक्टिक अभिलेख नियंत्रण',
  filterEvents: 'कार्यक्रम फ़िल्टर करें',
  clearAll: 'सभी हटाएँ',
  clearAllCount: 'सभी हटाएँ ({{count}})',
  search: 'खोजें',
  searchPlaceholder: 'नाम, ID, स्थान, पोर्टल कोड…',
  eventType: 'कार्यक्रम प्रकार',
  allEventTypes: 'सभी कार्यक्रम प्रकार',
  host: 'आयोजक',
  allHosts: 'सभी आयोजक',
  galaxy: 'गैलेक्सी',
  allGalaxies: 'सभी गैलेक्सी',
  dateContext: 'दिनांक अवधि',
  allDates: 'सभी दिनांक',
  upcomingOnly: 'केवल आगामी',
  next30Days: 'अगले 30 दिन',
  calendarPreferences: 'कैलेंडर प्राथमिकताएँ',
  closeSettings: 'सेटिंग बंद करें',
  changeLog: 'परिवर्तन लॉग',
  closeChangeLog: 'परिवर्तन लॉग बंद करें',
  loadingChangeLog: 'परिवर्तन लॉग लोड हो रहा है…',
  changeLogLoadError: 'परिवर्तन लॉग लोड नहीं किया जा सका।',
  noChangeLogEntries: 'कोई परिवर्तन लॉग प्रविष्टि उपलब्ध नहीं है।',
  fontScale: 'फ़ॉन्ट आकार',
  defaultLabel: 'डिफ़ॉल्ट',
  fontScaleHelp: 'पूरे कैलेंडर इंटरफ़ेस का आकार बदलता है।',
  locale: 'भाषा और क्षेत्र',
  localeHelp: 'स्थानीयकृत कैलेंडर दिनांक, समय और इंटरफ़ेस टेक्स्ट नियंत्रित करता है।',
  localTimezone: 'स्थानीय समय क्षेत्र',
  timeZoneHelp: 'कार्यक्रम का स्थान और स्थानीय समय-सारणी प्रदर्शन निर्धारित करता है।',
  agtAnthem: 'AGT गान',
  muteDefault: 'म्यूट (डिफ़ॉल्ट)',
  unmute: 'आवाज़ चालू करें',
  anthemHelp: 'आपकी पहली सहभागिता के बाद चलना शुरू होता है।',
  reset: 'रीसेट',
  import: 'आयात',
  export: 'निर्यात',
  settingsExported: 'सेटिंग निर्यात की गई।',
  settingsExportError: 'सेटिंग निर्यात नहीं की जा सकी।',
  settingsReset: 'सेटिंग रीसेट की गई।',
  settingsImported: 'सेटिंग आयात की गई।',
  settingsImportError: 'सेटिंग आयात नहीं की जा सकी।',
  autoTimeZone: 'स्वचालित',
  invalidSettingsJson: 'चुनी गई फ़ाइल मान्य सेटिंग JSON नहीं है।',
  settingsJsonObject: 'सेटिंग JSON में एक ऑब्जेक्ट होना चाहिए।',
  unsupportedFontScale: 'सेटिंग JSON में असमर्थित फ़ॉन्ट आकार है।',
  unsupportedLocale: 'सेटिंग JSON में असमर्थित भाषा है।',
  unsupportedTimezone: 'सेटिंग JSON में असमर्थित समय क्षेत्र है।',
  unsupportedAnthem: 'सेटिंग JSON में असमर्थित गान विकल्प है।',
  notSupplied: 'नहीं दिया गया',
  eventRecord: 'कार्यक्रम रिकॉर्ड · {{id}}',
  eventUrlCopied: 'कार्यक्रम URL कॉपी किया गया!',
  bookmarkAdded: 'बुकमार्क जोड़ा गया!',
  bookmarkRemoved: 'बुकमार्क हटाया गया!',
  copyUrlError: 'URL कॉपी नहीं किया जा सका',
  share: 'साझा करें',
  closeEventDetails: 'कार्यक्रम विवरण बंद करें',
  eventCoverImage: 'कार्यक्रम कवर चित्र',
  coverArtworkFor: '{{name}} का कवर चित्र',
  noEventCover: 'कोई कार्यक्रम कवर उपलब्ध नहीं है',
  coverLoadError: 'कवर चित्र लोड नहीं किया जा सका',
  noCoverSupplied: 'कोई कवर चित्र नहीं दिया गया',
  schedule: 'समय-सारणी',
  startLocal: 'आरंभ · स्थानीय',
  startUtc: 'आरंभ · UTC',
  endLocal: 'समाप्ति · स्थानीय',
  endUtc: 'समाप्ति · UTC',
  eventInformation: 'कार्यक्रम जानकारी',
  eventId: 'कार्यक्रम ID',
  region: 'क्षेत्र',
  system: 'सिस्टम',
  planet: 'ग्रह',
  portalCode: 'पोर्टल कोड',
  openGlyphTool: 'Glyph Tool में खोलें',
  description: 'विवरण',
  noMarkdownDescription: 'कोई Markdown विवरण नहीं दिया गया।',
  loadingRemoteDescription: 'दूरस्थ विवरण लोड हो रहा है…',
  remoteDescriptionError: 'दूरस्थ विवरण लोड नहीं किया जा सका। ऊपर दिए कार्यक्रम विवरण अभी भी उपलब्ध हैं।',
  relatedLinks: 'संबंधित लिंक',
  noRelatedLinks: 'कोई संबंधित लिंक नहीं दिया गया।',
  linkLabel: 'लिंक {{number}}',
  fieldRequired: '{{field}} आवश्यक है',
  eventIdHex: 'EVENT_ID में ठीक चार हेक्साडेसिमल वर्ण होने चाहिए',
  portalCodeLength: 'EVENT_PORTAL_CODE में ठीक 12 अक्षरांकीय वर्ण होने चाहिए',
  startUtcTimestamp: 'EVENT_DATETIME_START एक मान्य UTC ISO 8601 टाइमस्टैम्प होना चाहिए',
  endUtcTimestamp: 'EVENT_DATETIME_END एक मान्य UTC ISO 8601 टाइमस्टैम्प होना चाहिए',
  endBeforeStart: 'EVENT_DATETIME_END, EVENT_DATETIME_START से पहले नहीं हो सकता',
  unsafeDescriptionUrl: 'EVENT_MD_URI को अनदेखा किया गया क्योंकि यह सुरक्षित HTTP(S) URL नहीं है',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI को अनदेखा किया गया क्योंकि यह सुरक्षित HTTP(S) URL नहीं है',
  invalidRelatedLink: 'संबंधित लिंक अमान्य होने के कारण अनदेखा किया गया: {{value}}',
  csvParser: 'CSV पार्सर: {{message}}',
  missingHeaders: 'आवश्यक CSV हेडर अनुपस्थित हैं: {{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} दोहराया गया; पंक्ति छोड़ी गई',
};

const japanese: Translations = {
  ...COMMON_TRANSLATIONS,
  skipToCalendar: 'カレンダーへ移動',
  syncingDatabase: 'データベースを同期中',
  databaseOffline: 'データベースはオフラインです',
  databaseLive: 'データベースはオンラインです',
  openSettings: '設定を開く',
  settings: '設定',
  contactSupport: 'AGT サポートに連絡',
  addEvent: 'イベントを追加',
  bookmarks: 'ブックマーク',
  bookmarked: 'ブックマーク済み',
  bookmark: 'ブックマークに追加',
  removeBookmark: 'ブックマークを削除',
  closeBookmarks: 'ブックマークを閉じる',
  bookmarksBackupNotice: 'ブックマークはこのブラウザーにのみ保存され、クラウドには保存されません。閲覧データを消去すると失われる場合があります。「エクスポート」でバックアップし、「インポート」で復元できます。',
  noBookmarks: '保存したイベントはまだありません。',
  noMatchingBookmarks: '検索に一致するブックマークはありません。',
  missingBookmarks: '{{count}} 件のブックマークが存在しなくなったイベントを参照しています。',
  removeMissingBookmarks: '無効なブックマークを削除',
  bookmarksImported: 'ブックマークをインポートして統合しました。',
  bookmarksExported: 'ブックマークをエクスポートしました。',
  bookmarksCleared: 'ブックマークをすべて削除しました。',
  missingBookmarksRemoved: '無効なブックマークを削除しました。',
  bookmarksImportError: 'ブックマークをインポートできませんでした。',
  bookmarksExportError: 'ブックマークをエクスポートできませんでした。',
  bookmarksLoadError: '保存したブックマークを読み込めませんでした。',
  bookmarksSaveError: 'ブックマークを保存できませんでした。変更は適用されていません。',
  invalidBookmarksCsv: 'ブックマーク CSV には EVENT_ID 列のみを含め、各 ID は4桁の16進文字にしてください。',
  pageHeading: 'AGT 特別イベントカレンダー',
  pageIntro: '過去および今後の AGT 集会を検索・閲覧できます。イベントを選択すると、日程、ポータルアドレス、説明が表示されます。',
  syncHeading: '銀河アーカイブを同期中',
  syncDescription: 'イベントデータベースの最新コピーを取得しています…',
  databaseError: 'データベースエラー',
  eventsCouldNotBeLoaded: 'イベントを読み込めませんでした',
  databaseHttpError: 'イベントデータベースが HTTP {{status}} を返しました。',
  unexpectedDatabaseError: '予期しないデータベースエラーが発生しました。',
  databaseInstructions: '接続を確認し、設定された CSV が公開され、ブラウザーからのアクセスを許可していることを確認してください。',
  retrySync: '同期を再試行',
  databaseStatus: 'データベースの状態',
  validEventRecord: '件の有効なイベント',
  validEventRecords: '件の有効なイベント',
  matchingFilters: '件がフィルターに一致',
  lastSync: '最終同期',
  reloadDatabase: 'データベースを再読み込み',
  databaseLoadedWith: 'データベースを読み込みました：{{count}} 件の{{warningLabel}}{{skipped}}',
  warning: '警告',
  warnings: '警告',
  malformedRowsSkipped: '、不正な {{count}} 行をスキップ',
  validEventsRemain: '有効なイベントは引き続き利用できます。行番号は元の CSV を参照し、管理者向けに表示されています。',
  rowLabel: '{{row}} 行目：',
  archiveEmpty: 'アーカイブは空です',
  noValidEvents: '有効なイベントはありません',
  emptyArchiveDescription: 'CSV は正常に読み込まれましたが、必要なイベント形式を満たす行がありませんでした。',
  home: 'ホーム',
  about: '概要',
  team: 'チーム',
  contribute: '貢献',
  galacticArchives: '銀河アーカイブ',
  engage: '参加',
  terms: '利用規約',
  support: 'サポート',
  copyright: '著作権',
  copyrightNotice: '© {{year}} Alliance of Galactic Travellers',
  viewerLocalTime: '閲覧者の現地時間 · {{timeZone}}',
  calendarNavigation: 'カレンダーナビゲーション',
  previousMonth: '前の月',
  today: '今日',
  nextMonth: '次の月',
  goToMonth: '月へ移動',
  noMatchingEvents: 'このカレンダー表示に一致するイベントはありません。',
  matchingEventSummary: 'このカレンダー表示に一致するイベントは {{count}} 件です。',
  matchingEventsSummary: 'このカレンダー表示に一致するイベントは {{count}} 件です。',
  eventCalendar: 'イベントカレンダー',
  starts: '開始',
  continues: '継続',
  ends: '終了',
  event: 'イベント',
  eventDetails: '{{name}}。{{context}}。詳細を開く。',
  eventDetailsAtTime: '{{name}}。{{time}} に{{context}}。詳細を開く。',
  archiveControls: '銀河アーカイブの操作',
  filterEvents: 'イベントを絞り込む',
  clearAll: 'すべてクリア',
  clearAllCount: 'すべてクリア（{{count}}）',
  search: '検索',
  searchPlaceholder: '名前、ID、場所、ポータルコード…',
  eventType: 'イベントの種類',
  allEventTypes: 'すべてのイベント種類',
  host: '主催者',
  allHosts: 'すべての主催者',
  galaxy: '銀河',
  allGalaxies: 'すべての銀河',
  dateContext: '日付範囲',
  allDates: 'すべての日付',
  upcomingOnly: '今後のみ',
  next30Days: '今後 30 日',
  calendarPreferences: 'カレンダー設定',
  closeSettings: '設定を閉じる',
  changeLog: '変更履歴',
  closeChangeLog: '変更履歴を閉じる',
  loadingChangeLog: '変更履歴を読み込んでいます…',
  changeLogLoadError: '変更履歴を読み込めませんでした。',
  noChangeLogEntries: '変更履歴はありません。',
  fontScale: '文字サイズ',
  defaultLabel: '既定',
  fontScaleHelp: 'カレンダー画面全体を拡大縮小します。',
  locale: '言語と地域',
  localeHelp: 'カレンダーの日付、時刻、画面テキストのローカライズを設定します。',
  localTimezone: 'ローカルタイムゾーン',
  timeZoneHelp: 'イベントの配置と現地日程の表示を設定します。',
  agtAnthem: 'AGT アンセム',
  muteDefault: 'ミュート（既定）',
  unmute: 'ミュート解除',
  anthemHelp: '最初の操作後に再生が始まります。',
  reset: 'リセット',
  import: 'インポート',
  export: 'エクスポート',
  settingsExported: '設定をエクスポートしました。',
  settingsExportError: '設定をエクスポートできませんでした。',
  settingsReset: '設定をリセットしました。',
  settingsImported: '設定をインポートしました。',
  settingsImportError: '設定をインポートできませんでした。',
  autoTimeZone: '自動',
  invalidSettingsJson: '選択したファイルは有効な設定 JSON ではありません。',
  settingsJsonObject: '設定 JSON にはオブジェクトが必要です。',
  unsupportedFontScale: '設定 JSON に未対応の文字サイズが含まれています。',
  unsupportedLocale: '設定 JSON に未対応の言語が含まれています。',
  unsupportedTimezone: '設定 JSON に未対応のタイムゾーンが含まれています。',
  unsupportedAnthem: '設定 JSON に未対応のアンセム設定が含まれています。',
  notSupplied: '未指定',
  eventRecord: 'イベント記録 · {{id}}',
  eventUrlCopied: 'イベント URL をコピーしました！',
  bookmarkAdded: 'ブックマークを追加しました！',
  bookmarkRemoved: 'ブックマークを削除しました！',
  copyUrlError: 'URL をコピーできませんでした',
  share: '共有',
  closeEventDetails: 'イベント詳細を閉じる',
  eventCoverImage: 'イベントのカバー画像',
  coverArtworkFor: '{{name}} のカバー画像',
  noEventCover: 'イベントのカバー画像はありません',
  coverLoadError: 'カバー画像を読み込めませんでした',
  noCoverSupplied: 'カバー画像が指定されていません',
  schedule: '日程',
  startLocal: '開始 · 現地時間',
  startUtc: '開始 · UTC',
  endLocal: '終了 · 現地時間',
  endUtc: '終了 · UTC',
  eventInformation: 'イベント情報',
  eventId: 'イベント ID',
  region: '地域',
  system: '星系',
  planet: '惑星',
  portalCode: 'ポータルコード',
  openGlyphTool: 'Glyph Tool で開く',
  description: '説明',
  noMarkdownDescription: 'Markdown の説明はありません。',
  loadingRemoteDescription: '外部の説明を読み込み中…',
  remoteDescriptionError: '外部の説明を読み込めませんでした。上記のイベント詳細は引き続き利用できます。',
  relatedLinks: '関連リンク',
  noRelatedLinks: '関連リンクはありません。',
  linkLabel: 'リンク {{number}}',
  fieldRequired: '{{field}} は必須です',
  eventIdHex: 'EVENT_ID は 4 桁の 16 進文字である必要があります',
  portalCodeLength: 'EVENT_PORTAL_CODE は 12 桁の英数字である必要があります',
  startUtcTimestamp: 'EVENT_DATETIME_START は有効な UTC ISO 8601 タイムスタンプである必要があります',
  endUtcTimestamp: 'EVENT_DATETIME_END は有効な UTC ISO 8601 タイムスタンプである必要があります',
  endBeforeStart: 'EVENT_DATETIME_END を EVENT_DATETIME_START より前にすることはできません',
  unsafeDescriptionUrl: 'EVENT_MD_URI は安全な HTTP(S) URL ではないため無視されました',
  unsafeCoverUrl: 'EVENT_COVER_IMG_URI は安全な HTTP(S) URL ではないため無視されました',
  invalidRelatedLink: '無効な関連リンクを無視しました：{{value}}',
  csvParser: 'CSV パーサー：{{message}}',
  missingHeaders: '必須の CSV ヘッダーがありません：{{headers}}',
  duplicateEventId: 'EVENT_ID {{id}} が重複しているため、行をスキップしました',
};

export const TRANSLATIONS: Record<Locale, Translations> = {
  en: english,
  fr: french,
  de: german,
  es: spanish,
  pt: portuguese,
  it: italian,
  th: thai,
  zh: chinese,
  hi: hindi,
  ja: japanese,
};

export function getTranslations(locale: string): Translations {
  return TRANSLATIONS[locale as Locale] ?? english;
}

export function formatTranslation(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (placeholder, key: string) =>
    Object.hasOwn(values, key) ? String(values[key]) : placeholder,
  );
}
